import React, { useState, useEffect, useMemo, useRef } from 'react';
import { renderRichText } from '../utils/richText';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import api from '../services/api';
import { onNewSupportMessage, onNewUserRegistered, onUserReadMessages, onDepositTxidSubmitted, onWithdrawalCreated, onKycSubmitted, offEvent } from '../services/socket';
import { usdt } from '../utils/currency';
import { IconUsers, IconTrendingUp, IconWallet, IconClock, IconLock, IconChart, IconCalendar } from '../components/AdminIcons';
import ReferralTree from '../components/ReferralTree';
import './Admin.css';

// Obtenir l'offset Paris en heures (ex: 2 en été, 1 en hiver)
const getParisOffset = () => {
  const now = new Date();
  const utcStr = now.toLocaleString('en-US', { timeZone: 'UTC', hour: 'numeric', hour12: false });
  const parisStr = now.toLocaleString('en-US', { timeZone: 'Europe/Paris', hour: 'numeric', hour12: false });
  return parseInt(parisStr) - parseInt(utcStr);
};

// Convertir heure UTC vers heure Paris
const utcToParis = (hour, minute) => {
  const offset = getParisOffset();
  let parisHour = hour + offset;
  if (parisHour >= 24) parisHour -= 24;
  if (parisHour < 0) parisHour += 24;
  return { hour: parisHour, minute };
};

// Convertir heure Paris vers heure UTC
const parisToUtc = (hour, minute) => {
  const offset = getParisOffset();
  let utcHour = hour - offset;
  if (utcHour >= 24) utcHour -= 24;
  if (utcHour < 0) utcHour += 24;
  return { hour: utcHour, minute };
};

// Formater heure:minute en string "HH:MM"
const formatTime = (hour, minute) => {
  return `${String(hour).padStart(2, '0')}:${String(minute).padStart(2, '0')}`;
};

const Admin = () => {
  const { user } = useAuth();
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const tabFromUrl = searchParams.get('tab') || 'messages';

  const [activeTab, setActiveTab] = useState(tabFromUrl);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Dashboard stats
  const [stats, setStats] = useState(null);

  // Configs
  const [configs, setConfigs] = useState({});
  const [editingConfig, setEditingConfig] = useState(null);
  const [configValue, setConfigValue] = useState('');
  const [configHours, setConfigHours] = useState([]); // éditeur dédié distribution_hours

  // Users
  const [users, setUsers] = useState([]);
  const [userSearch, setUserSearch] = useState('');
  const [newUsersCount, setNewUsersCount] = useState(0);
  const [userSortBy, setUserSortBy] = useState('lastSeenAt'); // défaut: dernière activité
  const [userSortOrder, setUserSortOrder] = useState('desc');
  const [userPresence, setUserPresence] = useState(''); // '', online, active24h, active7d, inactive7d, inactive30d
  const [selectedUser, setSelectedUser] = useState(null);
  const [balanceAmount, setBalanceAmount] = useState('');
  const [transactionType, setTransactionType] = useState('credit');
  const [transactionReason, setTransactionReason] = useState('');
  const [blockReason, setBlockReason] = useState('');
  const [userTransactions, setUserTransactions] = useState([]);
  const [showTransactions, setShowTransactions] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showTransactionConfirm, setShowTransactionConfirm] = useState(false);
  const [pendingTransaction, setPendingTransaction] = useState(null);
  const [openAccordions, setOpenAccordions] = useState({ info: true });

  // Création admin/manager (master admin uniquement)
  const [showCreateAdmin, setShowCreateAdmin] = useState(false);
  const [newAdminForm, setNewAdminForm] = useState({ username: '', email: '', password: '', role: 'manager' });
  const [createAdminLoading, setCreateAdminLoading] = useState(false);
  const [createAdminError, setCreateAdminError] = useState('');

  // Withdrawals
  const [withdrawalsDashboard, setWithdrawalsDashboard] = useState(null);
  const [addressPending, setAddressPending] = useState([]);
  const [addressRejectingId, setAddressRejectingId] = useState(null);
  const [addressRejectReason, setAddressRejectReason] = useState('');
  const [addressActionLoading, setAddressActionLoading] = useState(null);
  const [withdrawalsList, setWithdrawalsList] = useState([]);
  const [withdrawalsLoading, setWithdrawalsLoading] = useState(false);
  const [selectedWithdrawal, setSelectedWithdrawal] = useState(null);
  const [copiedAddr, setCopiedAddr] = useState('');
  const [paymentRef, setPaymentRef] = useState('');
  const [paymentMethod, setPaymentMethod] = useState('');
  const [rejectionReason, setRejectionReason] = useState('');

  // Deposits decoder (code généré)
  const [depositTokenInput, setDepositTokenInput] = useState('');
  const [decodeLoading, setDecodeLoading] = useState(false);
  const [decodeError, setDecodeError] = useState('');
  const [decodedDepositResult, setDecodedDepositResult] = useState(null);
  const [depositCreditAmount, setDepositCreditAmount] = useState('');
  const [depositCreditReason, setDepositCreditReason] = useState('');
  const [depositCreditLoading, setDepositCreditLoading] = useState(false);
  const [depositCreditSuccess, setDepositCreditSuccess] = useState('');

  // Deposits par virement réel (TXID, réseau TRC20)
  const [pendingDeposits, setPendingDeposits] = useState([]);
  const [depositsTxidLoading, setDepositsTxidLoading] = useState(false);
  const [depositTxidRejectingId, setDepositTxidRejectingId] = useState(null);
  const [depositTxidRejectReason, setDepositTxidRejectReason] = useState('');
  const [depositTxidActionLoading, setDepositTxidActionLoading] = useState(null);
  const [newDepositsCount, setNewDepositsCount] = useState(0);
  // Vrais compteurs "en attente" (badges temps réel)
  const [pendingCounts, setPendingCounts] = useState({ withdrawals: 0, deposits: 0, kyc: 0 });
  const [depositAddressSetting, setDepositAddressSetting] = useState('');
  const [depositAddressInput, setDepositAddressInput] = useState('');
  const [depositAddressSaving, setDepositAddressSaving] = useState(false);
  // Statistiques de dépôts (par période)
  const [depositStats, setDepositStats] = useState(null);
  const [depositStatsPeriod, setDepositStatsPeriod] = useState('all'); // day|week|month|all|custom
  const [depositStatsMonth, setDepositStatsMonth] = useState(''); // 'YYYY-MM' si période = mois précis
  const [depositStatsLoading, setDepositStatsLoading] = useState(false);

  // KYC
  const [kycRequests, setKycRequests] = useState([]);
  const [kycStats, setKycStats] = useState(null);
  const [kycLoading, setKycLoading] = useState(false);
  const [kycRejectingId, setKycRejectingId] = useState(null);
  const [kycRejectReason, setKycRejectReason] = useState('');
  const [kycActionLoading, setKycActionLoading] = useState(null);

  // Messages
  const [messageConversations, setMessageConversations] = useState([]);
  const [messagesLoading, setMessagesLoading] = useState(false);
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [conversationMessages, setConversationMessages] = useState([]);
  const convMessagesRef = useRef(null); // conteneur des messages (auto-scroll bas)
  const [replyContent, setReplyContent] = useState('');
  const [replySending, setReplySending] = useState(false);
  const [totalUnreadMessages, setTotalUnreadMessages] = useState(0);
  const [showNewMsgSearch, setShowNewMsgSearch] = useState(false);
  const [newMsgQuery, setNewMsgQuery] = useState('');
  const [newMsgResults, setNewMsgResults] = useState([]);

  // Réattribution de parrain
  const [reassignCode, setReassignCode] = useState('');
  const [reassignSaving, setReassignSaving] = useState(false);

  // Notifications admin
  const [notifEmail, setNotifEmail] = useState('');
  const [notifSettings, setNotifSettings] = useState({});
  const [userNotifs, setUserNotifs] = useState({}); // toggles notifs utilisateurs (signal manqué)
  // Diffusion groupée (broadcast)
  const [bcAudience, setBcAudience] = useState({ verified: 'all', capital: 'all' });
  const [bcChannels, setBcChannels] = useState({ inapp: true, email: false });
  const [bcSubject, setBcSubject] = useState('');
  const [bcBody, setBcBody] = useState('');
  const [bcCount, setBcCount] = useState(null);
  const [bcSending, setBcSending] = useState(false);
  const [bcHistory, setBcHistory] = useState([]);
  // Portefeuilles épargne (admin)
  const [savingsList, setSavingsList] = useState([]);
  const [savingsUsers, setSavingsUsers] = useState([]); // pour les pickers (id, username, email)
  const [savingsForm, setSavingsForm] = useState({ name: '', owner: '', associate: '', amount: '' });
  const [notifLoading, setNotifLoading] = useState(false);
  const [notifEmailSaving, setNotifEmailSaving] = useState(false);
  const [notifSavedMsg, setNotifSavedMsg] = useState('');

  // Schedule (Planning mensuel)
  const [schedule, setSchedule] = useState(null);
  const [scheduleLoading, setScheduleLoading] = useState(false);
  const [selectedDay, setSelectedDay] = useState(null);
  const [daySlots, setDaySlots] = useState([]);
  const [editingSlot, setEditingSlot] = useState(null);
  const [slotEditForm, setSlotEditForm] = useState({});
  const [regenerating, setRegenerating] = useState(false);
  const [scheduleMonth, setScheduleMonth] = useState(() => {
    const n = new Date();
    return `${n.getFullYear()}-${String(n.getMonth() + 1).padStart(2, '0')}`;
  });
  // Liste des mois du planning (statut passé/courant/futur + suppression différée)
  const [scheduleMonths, setScheduleMonths] = useState([]);
  const [scheduleGraceDays, setScheduleGraceDays] = useState(20);
  const [generatingMonths, setGeneratingMonths] = useState(false);

  // Slot users (pour voir qui a accepté/rejeté ou éligibles)
  const [viewingSlotUsers, setViewingSlotUsers] = useState(null);
  const [slotUsers, setSlotUsers] = useState([]);
  const [notEligibleUsers, setNotEligibleUsers] = useState([]);
  const [slotUsersLoading, setSlotUsersLoading] = useState(false);
  const [slotUsersFilters, setSlotUsersFilters] = useState({
    accepted: true,
    rejected: true,
    expired: true,
    pending: true,
    eligible: true,
    notEligible: false
  });

  // Check admin access
  useEffect(() => {
    if (user && !['admin', 'manager'].includes(user.role)) {
      navigate('/dashboard');
    }
  }, [user, navigate]);

  // Synchroniser activeTab avec l'URL quand elle change
  useEffect(() => {
    setActiveTab(tabFromUrl);
  }, [tabFromUrl]);

  // Fonction pour changer de tab et mettre à jour l'URL
  const handleTabChange = (tab) => {
    setActiveTab(tab);
    setSearchParams({ tab });
  };

  // Charger le compteur de messages au démarrage
  useEffect(() => {
    loadMessages();
  }, []);

  // Load dashboard data
  useEffect(() => {
    if (activeTab === 'dashboard') {
      loadDashboard();
    } else if (activeTab === 'config') {
      loadConfigs();
    } else if (activeTab === 'users') {
      loadUsers();
    } else if (activeTab === 'withdrawals') {
      loadWithdrawals();
    } else if (activeTab === 'kyc') {
      loadKyc();
    } else if (activeTab === 'deposits') {
      setDecodedDepositResult(null);
      setDepositTokenInput('');
      setDecodeError('');
      setDepositCreditSuccess('');
      loadPendingDeposits();
      loadDepositStats();
      setNewDepositsCount(0);
    } else if (activeTab === 'schedule') {
      loadSchedule();
      loadScheduleMonths();
    } else if (activeTab === 'messages') {
      loadMessages();
    } else if (activeTab === 'notifications') {
      loadNotifications();
    } else if (activeTab === 'broadcast') {
      loadBcCount();
      loadBcHistory();
    } else if (activeTab === 'savings') {
      loadSavingsAdmin();
    }
  }, [activeTab]);

  // Auto-scroll vers le DERNIER message à l'ouverture d'une conversation et à
  // chaque nouveau message (sinon on tombe sur le plus ancien — pénible sur mobile).
  useEffect(() => {
    const el = convMessagesRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [conversationMessages, selectedConversation?.id]);

  // Charger les vrais compteurs "en attente" (badges)
  const loadPendingCounts = async () => {
    try {
      const res = await api.get('/admin/pending-counts');
      setPendingCounts({
        withdrawals: res.data.withdrawals || 0,
        deposits: res.data.deposits || 0,
        kyc: res.data.kyc || 0
      });
    } catch (e) { /* silencieux */ }
  };

  // Au démarrage + rafraîchissement périodique (filet de sécurité en plus du temps réel)
  useEffect(() => {
    loadPendingCounts();
    const iv = setInterval(loadPendingCounts, 45000);
    return () => clearInterval(iv);
  }, []);

  // Temps réel: nouvelle demande de retrait / soumission KYC → recompter
  useEffect(() => {
    const refresh = () => loadPendingCounts();
    onWithdrawalCreated(refresh);
    onKycSubmitted(refresh);
    return () => { offEvent('withdrawal_created'); offEvent('kyc_submitted'); };
  }, []);

  // Écouter les nouveaux messages support en temps réel
  useEffect(() => {
    const handleNewSupportMessage = (data) => {
      // Mettre à jour la liste des conversations
      loadMessages();
      // Si on a la conversation de cet utilisateur ouverte, ajouter le message
      if (selectedConversation?.id === data.userId) {
        setConversationMessages(prev => [...prev, data.message]);
      }
    };

    onNewSupportMessage(handleNewSupportMessage);

    return () => {
      offEvent('new_support_message');
    };
  }, [selectedConversation]);

  // Écouter les nouveaux utilisateurs inscrits
  useEffect(() => {
    const handleNewUser = (data) => {
      setNewUsersCount(prev => prev + 1);
      // Si on est sur l'onglet users, recharger la liste
      if (activeTab === 'users') {
        loadUsers();
      }
    };

    onNewUserRegistered(handleNewUser);

    return () => {
      offEvent('new_user_registered');
    };
  }, [activeTab]);

  // Écouter la confirmation de lecture par l'utilisateur (temps réel)
  useEffect(() => {
    const handleUserRead = (data) => {
      // Mettre à jour la conversation si elle est ouverte
      if (selectedConversation?.id === data.userId) {
        setConversationMessages(prev =>
          prev.map(m => m.senderRole === 'admin' && !m.readAt ? { ...m, readAt: data.readAt } : m)
        );
      }
    };

    onUserReadMessages(handleUserRead);

    return () => {
      offEvent('user_read_messages');
    };
  }, [selectedConversation]);

  // Écouter les soumissions de TXID de dépôt en temps réel
  useEffect(() => {
    const handleDepositTxidSubmitted = () => {
      setNewDepositsCount(prev => prev + 1);
      loadPendingCounts();
      if (activeTab === 'deposits') {
        loadPendingDeposits();
      }
    };

    onDepositTxidSubmitted(handleDepositTxidSubmitted);

    return () => {
      offEvent('deposit_txid_submitted');
    };
  }, [activeTab]);

  const loadDashboard = async () => {
    try {
      setLoading(true);
      const response = await api.get('/admin/dashboard');
      setStats(response.data.stats);
    } catch (err) {
      setError(err.response?.data?.error || 'Erreur de chargement');
    } finally {
      setLoading(false);
    }
  };

  const loadConfigs = async () => {
    try {
      setLoading(true);
      const response = await api.get('/admin/config');
      setConfigs(response.data.configs);
    } catch (err) {
      setError(err.response?.data?.error || 'Erreur de chargement');
    } finally {
      setLoading(false);
    }
  };

  const loadUsers = async () => {
    try {
      setLoading(true);
      const response = await api.get('/admin/users', {
        params: { search: userSearch, limit: 100, ...(userPresence ? { presence: userPresence } : {}) }
      });
      setUsers(response.data.users);
    } catch (err) {
      setError(err.response?.data?.error || 'Erreur de chargement');
    } finally {
      setLoading(false);
    }
  };

  // Recharger la liste quand le filtre de présence change (filtrage serveur)
  useEffect(() => {
    if (activeTab === 'users') loadUsers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userPresence]);

  // Tri des utilisateurs
  const sortedUsers = useMemo(() => {
    if (!users.length) return [];
    return [...users].sort((a, b) => {
      let valA, valB;
      switch (userSortBy) {
        case 'balance':
          valA = parseFloat(a.balance) || 0;
          valB = parseFloat(b.balance) || 0;
          break;
        case 'lastSeenAt':
          valA = a.lastSeenAt ? new Date(a.lastSeenAt).getTime() : 0;
          valB = b.lastSeenAt ? new Date(b.lastSeenAt).getTime() : 0;
          break;
        case 'capitalConfirmedAt':
          valA = a.capitalConfirmedAt ? new Date(a.capitalConfirmedAt).getTime() : 0;
          valB = b.capitalConfirmedAt ? new Date(b.capitalConfirmedAt).getTime() : 0;
          break;
        case 'rank':
          const rankOrder = { diamond: 5, platinum: 4, gold: 3, silver: 2, bronze: 1 };
          valA = rankOrder[a.rank] || 0;
          valB = rankOrder[b.rank] || 0;
          break;
        case 'createdAt':
        default:
          valA = new Date(a.createdAt).getTime();
          valB = new Date(b.createdAt).getTime();
      }
      return userSortOrder === 'desc' ? valB - valA : valA - valB;
    });
  }, [users, userSortBy, userSortOrder]);

  const loadWithdrawals = async () => {
    try {
      setWithdrawalsLoading(true);
      const [dashboardRes, listRes, addrRes] = await Promise.all([
        api.get('/admin/withdrawals/dashboard'),
        api.get('/admin/withdrawals'),
        api.get('/admin/withdrawal-addresses?status=pending')
      ]);
      setWithdrawalsDashboard(dashboardRes.data.dashboard);
      setWithdrawalsList(listRes.data.withdrawals);
      setAddressPending(addrRes.data.users || []);
    } catch (err) {
      setError(err.response?.data?.error || 'Erreur de chargement');
    } finally {
      setWithdrawalsLoading(false);
    }
  };

  // Valider / rejeter une adresse de retrait
  const handleWithdrawalAddress = async (userId, action, reason) => {
    try {
      setAddressActionLoading(userId);
      await api.post(`/admin/withdrawal-addresses/${userId}/${action}`, action === 'reject' ? { reason } : {});
      setAddressRejectingId(null);
      setAddressRejectReason('');
      await loadWithdrawals();
    } catch (err) {
      alert(err.response?.data?.error || 'Erreur');
    } finally {
      setAddressActionLoading(null);
    }
  };

  // Charger les dépôts TXID en attente + l'adresse de dépôt configurée
  const loadPendingDeposits = async () => {
    try {
      setDepositsTxidLoading(true);
      const [pendingRes, settingsRes] = await Promise.all([
        api.get('/admin/deposits/pending'),
        api.get('/admin/deposits/settings')
      ]);
      setPendingDeposits(pendingRes.data.deposits || []);
      setDepositAddressSetting(settingsRes.data.address || '');
    } catch (err) {
      setError(err.response?.data?.error || 'Erreur de chargement des dépôts');
    } finally {
      setDepositsTxidLoading(false);
    }
  };

  // Charger les statistiques de dépôts selon la période sélectionnée
  const loadDepositStats = async (period = depositStatsPeriod, month = depositStatsMonth) => {
    try {
      setDepositStatsLoading(true);
      const params = (period === 'custom' && month) ? { month } : { period };
      const res = await api.get('/admin/deposits/stats', { params });
      setDepositStats(res.data);
    } catch (err) {
      // stats secondaires: ne pas bloquer la page
    } finally {
      setDepositStatsLoading(false);
    }
  };

  const handleDepositPeriod = (period) => {
    setDepositStatsPeriod(period);
    if (period !== 'custom') { setDepositStatsMonth(''); loadDepositStats(period, ''); }
  };

  // Valider / rejeter un dépôt TXID
  const handleDepositTxid = async (id, action, reason) => {
    try {
      setDepositTxidActionLoading(id);
      await api.post(`/admin/deposits/${id}/${action}`, action === 'reject' ? { reason } : {});
      setDepositTxidRejectingId(null);
      setDepositTxidRejectReason('');
      await loadPendingDeposits();
      loadPendingCounts();
    } catch (err) {
      alert(err.response?.data?.error || 'Erreur');
    } finally {
      setDepositTxidActionLoading(null);
    }
  };

  // Enregistrer l'adresse TRC20 affichée aux utilisateurs pour les dépôts
  const handleSaveDepositAddress = async () => {
    try {
      setDepositAddressSaving(true);
      await api.put('/admin/deposits/settings', { address: depositAddressInput.trim() });
      setDepositAddressSetting(depositAddressInput.trim());
      setDepositAddressInput('');
    } catch (err) {
      alert(err.response?.data?.error || 'Erreur');
    } finally {
      setDepositAddressSaving(false);
    }
  };

  const loadKyc = async () => {
    try {
      setKycLoading(true);
      const [requestsRes, statsRes] = await Promise.all([
        api.get('/admin/kyc'),
        api.get('/admin/kyc/stats')
      ]);
      setKycRequests(requestsRes.data.users || []);
      setKycStats(statsRes.data.stats);
    } catch (err) {
      setError(err.response?.data?.error || 'Erreur de chargement KYC');
    } finally {
      setKycLoading(false);
    }
  };

  const loadMessages = async () => {
    try {
      setMessagesLoading(true);
      const response = await api.get('/admin/messages');
      const conversations = response.data.conversations || [];
      setMessageConversations(conversations);
      // Calculer le total des messages non lus
      const total = conversations.reduce((sum, c) => sum + (c.unreadCount || 0), 0);
      setTotalUnreadMessages(total);
    } catch (err) {
      setError(err.response?.data?.error || 'Erreur de chargement des messages');
    } finally {
      setMessagesLoading(false);
    }
  };

  // ── Notifications admin ──
  const NOTIF_EVENTS = [
    { key: 'nouvelUtilisateur', icon: '👤', label: 'Nouvel utilisateur', desc: 'Un compte vient d\'être créé' },
    { key: 'depot', icon: '💰', label: 'Demande de dépôt', desc: 'Un TXID de virement a été soumis' },
    { key: 'retrait', icon: '🏧', label: 'Demande de retrait', desc: 'Une demande de retrait a été créée' },
    { key: 'message', icon: '💬', label: 'Nouveau message support', desc: 'Un utilisateur a écrit au support' },
    { key: 'capitalConfirme', icon: '📈', label: 'Capital confirmé', desc: 'Un utilisateur a réellement investi' },
    { key: 'kyc', icon: '🪪', label: 'KYC à valider', desc: 'Une pièce d\'identité a été soumise' },
    { key: 'adresseRetrait', icon: '🏦', label: 'Adresse de retrait à valider', desc: 'Une adresse TRC20 a été soumise' }
  ];

  const loadNotifications = async () => {
    try {
      setNotifLoading(true);
      const res = await api.get('/admin/notifications');
      setNotifEmail(res.data.email || '');
      setNotifSettings(res.data.settings || {});
      setUserNotifs(res.data.userNotifications || {});
    } catch (err) {
      setError(err.response?.data?.error || 'Erreur de chargement des notifications');
    } finally {
      setNotifLoading(false);
    }
  };

  const toggleNotif = async (key) => {
    const next = { ...notifSettings, [key]: !notifSettings[key] };
    setNotifSettings(next); // maj optimiste
    try {
      await api.put('/admin/notifications', { settings: { [key]: next[key] } });
    } catch (err) {
      setNotifSettings(notifSettings); // rollback
      alert(err.response?.data?.error || 'Erreur');
    }
  };

  const toggleUserNotif = async (field) => {
    const next = { ...userNotifs, [field]: !userNotifs[field] };
    setUserNotifs(next); // maj optimiste
    try {
      await api.put('/admin/notifications', { userNotifications: { [field]: next[field] } });
    } catch (err) {
      setUserNotifs(userNotifs); // rollback
      alert(err.response?.data?.error || 'Erreur');
    }
  };

  const USER_NOTIF_EVENTS = [
    { key: 'missedSignalParrain', icon: '💬', label: 'Signal manqué → message au parrain', desc: 'Message in-app au parrain à chaque signal raté par un filleul (aucun email)' },
    { key: 'missedSignalEmail', icon: '📧', label: 'Signal manqué → email à l\'utilisateur', desc: 'Email à l\'utilisateur qui a raté un signal, au plus 1 fois par jour' },
    { key: 'withdrawalPaid', icon: '💸', label: 'Retrait payé → email à l\'utilisateur', desc: 'Email de confirmation à l\'utilisateur quand son retrait est validé et payé' }
  ];

  const loadBcCount = async (audience = bcAudience) => {
    try {
      const res = await api.get('/admin/broadcast/count', { params: audience });
      setBcCount(res.data.count);
    } catch (err) { setBcCount(null); }
  };

  const loadBcHistory = async () => {
    try {
      const res = await api.get('/admin/broadcast/history');
      setBcHistory(res.data.broadcasts || []);
    } catch (err) { /* secondaire */ }
  };

  const setBcAud = (patch) => {
    const next = { ...bcAudience, ...patch };
    setBcAudience(next);
    loadBcCount(next);
  };

  const sendBroadcast = async () => {
    if (!bcBody.trim()) { alert('Le message est requis.'); return; }
    if (!bcChannels.inapp && !bcChannels.email) { alert('Choisissez au moins un canal (messagerie ou email).'); return; }
    if (bcChannels.email && !bcSubject.trim()) { alert('L\'objet est requis pour l\'email.'); return; }
    const canaux = [bcChannels.inapp && 'messagerie in-app', bcChannels.email && 'email'].filter(Boolean).join(' + ');
    if (!window.confirm(`Envoyer ce message à ${bcCount ?? '?'} utilisateur(s) via ${canaux} ?`)) return;
    setBcSending(true);
    try {
      const res = await api.post('/admin/broadcast', {
        subject: bcSubject, body: bcBody,
        audience: bcAudience, channels: bcChannels
      });
      alert(res.data.message || 'Diffusion envoyée');
      setBcSubject(''); setBcBody('');
      loadBcHistory();
    } catch (err) {
      alert(err.response?.data?.error || 'Erreur lors de la diffusion');
    } finally {
      setBcSending(false);
    }
  };

  // ---- Portefeuilles épargne (admin) ----
  const loadSavingsAdmin = async () => {
    try { const res = await api.get('/admin/savings'); setSavingsList(res.data.wallets || []); } catch (e) { /* */ }
  };
  const searchSavingsUsers = async (q) => {
    if (!q || q.trim().length < 2) { setSavingsUsers([]); return; }
    try { const res = await api.get(`/admin/users?search=${encodeURIComponent(q.trim())}&limit=8`); setSavingsUsers(res.data.users || []); } catch (e) { /* */ }
  };
  const resolveSavingsUser = async (text) => {
    const t = (text || '').trim(); if (!t) return null;
    const res = await api.get(`/admin/users?search=${encodeURIComponent(t)}&limit=5`);
    const list = res.data.users || [];
    return list.find(u => u.username === t || u.email === t) || (list.length === 1 ? list[0] : null);
  };
  const handleCreateSavings = async () => {
    if (!savingsForm.name.trim()) { alert('Le nom est requis'); return; }
    const owner = await resolveSavingsUser(savingsForm.owner);
    if (!owner) { alert('Propriétaire introuvable — tapez un pseudo ou email exact'); return; }
    let associateId = null;
    if (savingsForm.associate.trim()) {
      const a = await resolveSavingsUser(savingsForm.associate);
      if (!a) { alert('Associé introuvable'); return; }
      associateId = a.id;
    }
    try {
      const amt = parseFloat(String(savingsForm.amount).replace(',', '.')) || 0;
      await api.post('/admin/savings', { name: savingsForm.name.trim(), ownerId: owner.id, associateId, amount: amt });
      setSavingsForm({ name: '', owner: '', associate: '', amount: '' });
      setSavingsUsers([]);
      loadSavingsAdmin();
    } catch (e) { alert(e.response?.data?.error || 'Erreur lors de la création'); }
  };
  const adjustSavingsWallet = async (id, sign) => {
    const v = prompt(sign > 0 ? 'Montant à créditer (USDT)' : 'Montant à débiter (USDT)');
    if (v === null) return;
    const amount = parseFloat(String(v).replace(',', '.'));
    if (!(amount > 0)) { alert('Montant invalide'); return; }
    try { await api.post(`/admin/savings/${id}/${sign > 0 ? 'credit' : 'debit'}`, { amount }); loadSavingsAdmin(); }
    catch (e) { alert(e.response?.data?.error || 'Erreur'); }
  };
  const setSavingsAssociate = async (id) => {
    const v = prompt('Pseudo/email du nouvel associé (laisser vide pour retirer l\'associé)');
    if (v === null) return;
    let associateId = null;
    if (v.trim()) { const a = await resolveSavingsUser(v); if (!a) { alert('Utilisateur introuvable'); return; } associateId = a.id; }
    try { await api.put(`/admin/savings/${id}/associate`, { associateId }); loadSavingsAdmin(); }
    catch (e) { alert(e.response?.data?.error || 'Erreur'); }
  };
  const deleteSavingsWallet = async (w) => {
    const warn = w.balance > 0
      ? `⚠️ ATTENTION : ce portefeuille contient encore ${w.balance.toFixed(2)} USDT.\n\nLe supprimer effacera ce solde définitivement.\n\nSupprimer "${w.name}" quand même ?`
      : `Supprimer le portefeuille "${w.name}" ?`;
    if (!window.confirm(warn)) return;
    try { await api.delete(`/admin/savings/${w.id}`); loadSavingsAdmin(); }
    catch (e) { alert(e.response?.data?.error || 'Erreur'); }
  };

  const saveNotifEmail = async () => {
    try {
      setNotifEmailSaving(true);
      setNotifSavedMsg('');
      const res = await api.put('/admin/notifications', { email: notifEmail });
      setNotifEmail(res.data.email || '');
      setNotifSavedMsg('Email enregistré ✓');
      setTimeout(() => setNotifSavedMsg(''), 2500);
    } catch (err) {
      alert(err.response?.data?.error || 'Erreur');
    } finally {
      setNotifEmailSaving(false);
    }
  };

  const loadConversation = async (userId) => {
    try {
      const response = await api.get(`/admin/messages/${userId}`);
      setConversationMessages(response.data.messages || []);
      setSelectedConversation(response.data.user);
    } catch (err) {
      alert(err.response?.data?.error || 'Erreur');
    }
  };

  // Nouveau message: recherche d'un utilisateur pour initier une conversation
  // (le backend accepte déjà /reply vers n'importe quel user, même sans historique)
  const handleNewMsgSearch = async (query) => {
    setNewMsgQuery(query);
    if (query.trim().length < 2) {
      setNewMsgResults([]);
      return;
    }
    try {
      const res = await api.get(`/admin/users?search=${encodeURIComponent(query)}&limit=8`);
      setNewMsgResults(res.data.users || []);
    } catch (err) {
      setNewMsgResults([]);
    }
  };

  const handleNewMsgSelect = (userId) => {
    setShowNewMsgSearch(false);
    setNewMsgQuery('');
    setNewMsgResults([]);
    loadConversation(userId);
  };

  const handleReply = async () => {
    if (!replyContent.trim() || !selectedConversation) return;
    try {
      setReplySending(true);
      const res = await api.post(`/admin/messages/${selectedConversation.id}/reply`, {
        content: replyContent
      });
      // Ajouter le message directement à la liste (temps réel)
      if (res.data.message) {
        setConversationMessages(prev => [
          // Marquer les messages utilisateur comme lus
          ...prev.map(m => m.senderRole === 'user' ? { ...m, isRead: true, readAt: new Date().toISOString() } : m),
          // Ajouter le nouveau message admin
          { ...res.data.message, senderName: 'Support' }
        ]);
      }
      setReplyContent('');
      loadMessages(); // Mettre à jour la liste des conversations
    } catch (err) {
      alert(err.response?.data?.error || 'Erreur');
    } finally {
      setReplySending(false);
    }
  };

  const handleDeleteConversation = async (userId) => {
    if (!window.confirm('Supprimer cette conversation ?')) return;
    try {
      await api.delete(`/admin/messages/${userId}`);
      setSelectedConversation(null);
      setConversationMessages([]);
      loadMessages();
    } catch (err) {
      alert(err.response?.data?.error || 'Erreur');
    }
  };

  const loadSchedule = async (month = scheduleMonth) => {
    try {
      setScheduleLoading(true);
      const response = await api.get(`/admin/schedule?month=${month}`);
      setSchedule(response.data);
      setSelectedDay(null);
      setDaySlots([]);
    } catch (err) {
      setError(err.response?.data?.error || 'Erreur de chargement du planning');
    } finally {
      setScheduleLoading(false);
    }
  };

  const loadDaySlots = async (day, month = scheduleMonth) => {
    try {
      const response = await api.get(`/admin/schedule/day/${day}?month=${month}`);
      setDaySlots(response.data.slots);
      setSelectedDay(day);
      setEditingSlot(null);
    } catch (err) {
      alert(err.response?.data?.error || 'Erreur');
    }
  };

  const loadSlotUsers = async (slotId) => {
    setSlotUsersLoading(true);
    try {
      const response = await api.get(`/admin/schedule/${slotId}/users`);
      setSlotUsers(response.data.users);
      setNotEligibleUsers(response.data.notEligible || []);
      setViewingSlotUsers(response.data.slot);
    } catch (err) {
      alert(err.response?.data?.error || 'Erreur');
    } finally {
      setSlotUsersLoading(false);
    }
  };

  const closeSlotUsers = () => {
    setViewingSlotUsers(null);
    setSlotUsers([]);
    setNotEligibleUsers([]);
  };

  const filteredSlotUsers = useMemo(() => {
    if (!viewingSlotUsers) return [];
    if (viewingSlotUsers.distributed) {
      return slotUsers.filter(u => {
        if (u.status === 'accepted' || u.status === 'completed') return slotUsersFilters.accepted;
        if (u.status === 'rejected') return slotUsersFilters.rejected;
        if (u.status === 'expired') return slotUsersFilters.expired;
        if (u.status === 'pending') return slotUsersFilters.pending;
        return true;
      });
    } else {
      const eligible = slotUsersFilters.eligible ? slotUsers : [];
      const notElig = slotUsersFilters.notEligible ? notEligibleUsers : [];
      return [...eligible, ...notElig];
    }
  }, [viewingSlotUsers, slotUsers, notEligibleUsers, slotUsersFilters]);

  const handleEditSlot = (slot) => {
    setEditingSlot(slot.id);
    const [hour, minute] = slot.hour.split(':').map(Number);
    // Convertir UTC → Paris pour l'affichage
    const paris = utcToParis(hour, minute);
    setSlotEditForm({
      actualGainPercent: slot.actualGain,
      potentialGainPercent: slot.potentialGain,
      crypto: slot.crypto,
      type: slot.type,
      slotHour: paris.hour,
      slotMinute: paris.minute,
      startDelayMinutes: slot.startDelayMinutes || 20
    });
  };

  const handleSaveSlot = async () => {
    if (!editingSlot) return;
    try {
      // Convertir Paris → UTC pour l'envoi au backend
      const utc = parisToUtc(slotEditForm.slotHour, slotEditForm.slotMinute);
      const dataToSend = {
        ...slotEditForm,
        slotHour: utc.hour,
        slotMinute: utc.minute
      };
      await api.put(`/admin/schedule/${editingSlot}`, dataToSend);
      alert('Slot modifié avec succès!');
      setEditingSlot(null);
      loadDaySlots(selectedDay);
      loadSchedule();
    } catch (err) {
      alert(err.response?.data?.error || 'Erreur lors de la modification');
    }
  };

  const handleRegenerateFuture = async () => {
    if (!window.confirm(t('admin.regenerateConfirm'))) return;
    setRegenerating(true);
    try {
      const res = await api.post('/admin/schedule/regenerate-future');
      alert(`${res.data.regenerated} slots régénérés, ${res.data.protected} protégés`);
      loadSchedule();
      if (selectedDay) loadDaySlots(selectedDay);
    } catch (err) {
      alert(err.response?.data?.error || 'Erreur lors de la régénération');
    } finally {
      setRegenerating(false);
    }
  };

  const loadScheduleMonths = async () => {
    try {
      const res = await api.get('/admin/schedule/months');
      setScheduleMonths(res.data.months || []);
      if (res.data.graceDays) setScheduleGraceDays(res.data.graceDays);
    } catch (err) {
      // silencieux: la liste des mois est secondaire
    }
  };

  const handleGenerateMonths = async () => {
    if (!window.confirm('Générer le planning sur le mois courant + 12 mois suivants ? Les mois passés ne seront pas touchés, les slots déjà distribués sont protégés.')) return;
    setGeneratingMonths(true);
    try {
      const res = await api.post('/admin/schedule/generate-months', { monthsAhead: 12 });
      alert(res.data.message || 'Planning généré');
      await Promise.all([loadSchedule(), loadScheduleMonths()]);
    } catch (err) {
      alert(err.response?.data?.error || 'Erreur lors de la génération');
    } finally {
      setGeneratingMonths(false);
    }
  };

  const handleDeleteMonth = async (monthYear) => {
    if (!window.confirm(`Supprimer le mois ${monthYear} ? Il sera définitivement effacé dans ${scheduleGraceDays} jours et reste restaurable jusque-là.`)) return;
    try {
      const res = await api.post(`/admin/schedule/months/${monthYear}/delete`);
      alert(res.data.message || 'Mois programmé pour suppression');
      loadScheduleMonths();
    } catch (err) {
      alert(err.response?.data?.error || 'Erreur lors de la suppression du mois');
    }
  };

  const handleRestoreMonth = async (monthYear) => {
    try {
      const res = await api.post(`/admin/schedule/months/${monthYear}/restore`);
      alert(res.data.message || 'Mois restauré');
      loadScheduleMonths();
    } catch (err) {
      alert(err.response?.data?.error || 'Erreur lors de la restauration');
    }
  };

  const handleDecodeDepositToken = async () => {
    if (!depositTokenInput.trim()) return;
    setDecodeLoading(true);
    setDecodeError('');
    setDecodedDepositResult(null);
    setDepositCreditSuccess('');
    setDepositCreditAmount('');
    try {
      const res = await api.post('/admin/deposits/decode', { token: depositTokenInput.trim() });
      setDecodedDepositResult(res.data);
    } catch (err) {
      setDecodeError(err.response?.data?.error || 'Erreur lors du décodage');
    } finally {
      setDecodeLoading(false);
    }
  };

  const handleCreditFromDeposit = async () => {
    if (!decodedDepositResult || !depositCreditAmount || parseFloat(depositCreditAmount) <= 0) return;
    setDepositCreditLoading(true);
    setDepositCreditSuccess('');
    try {
      const userId = decodedDepositResult.user.id;
      const amount = parseFloat(depositCreditAmount);
      const reason = depositCreditReason || 'Dépôt via token';
      await api.post(`/admin/users/${userId}/add-balance`, { amount, reason });
      setDepositCreditSuccess(`${amount.toFixed(2)} USDT crédités sur le compte de ${decodedDepositResult.user.username}`);
      setDepositCreditAmount('');
      setDepositCreditReason('');
      // Rafraîchir le résultat décodé
      const res = await api.post('/admin/deposits/decode', { token: depositTokenInput.trim() });
      setDecodedDepositResult(res.data);
    } catch (err) {
      setDecodeError(err.response?.data?.error || 'Erreur lors du crédit');
    } finally {
      setDepositCreditLoading(false);
    }
  };

  const handleVerifyKyc = async (userId) => {
    try {
      setKycActionLoading(userId + '_verify');
      await api.post(`/admin/kyc/${userId}/verify`);
      loadKyc();
      loadPendingCounts();
    } catch (err) {
      alert(err.response?.data?.error || 'Erreur lors de la vérification');
    } finally {
      setKycActionLoading(null);
    }
  };

  const handleRejectKyc = async (userId) => {
    if (!kycRejectReason.trim()) {
      alert('Veuillez entrer une raison de rejet');
      return;
    }
    try {
      setKycActionLoading(userId + '_reject');
      await api.post(`/admin/kyc/${userId}/reject`, { reason: kycRejectReason });
      setKycRejectingId(null);
      setKycRejectReason('');
      loadKyc();
      loadPendingCounts();
    } catch (err) {
      alert(err.response?.data?.error || 'Erreur lors du rejet');
    } finally {
      setKycActionLoading(null);
    }
  };

  const copyToClipboard = (text) => {
    if (!text) return;
    navigator.clipboard?.writeText(text).then(() => {
      setCopiedAddr(text);
      setTimeout(() => setCopiedAddr(''), 1600);
    }).catch(() => {});
  };

  // Bloc "adresse TRC20 copiable" réutilisable (paiement du client)
  const AddressCopyBlock = ({ address }) => address ? (
    <div style={{ margin: '10px 0', padding: '10px 12px', borderRadius: 8, background: 'rgba(240,185,11,0.08)', border: '1px solid rgba(240,185,11,0.35)' }}>
      <div style={{ fontSize: 12, opacity: 0.75, marginBottom: 4 }}>Adresse de retrait (TRC20) — à utiliser pour payer</div>
      <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
        <code style={{ flex: 1, minWidth: 200, wordBreak: 'break-all', fontSize: 13 }}>{address}</code>
        <button type="button" onClick={() => copyToClipboard(address)}
          style={{ padding: '6px 12px', borderRadius: 6, cursor: 'pointer', border: '1px solid var(--accent,#f0b90b)', background: copiedAddr === address ? '#0ecb81' : 'transparent', color: copiedAddr === address ? '#04211d' : 'inherit', fontWeight: 600, whiteSpace: 'nowrap' }}>
          {copiedAddr === address ? '✓ Copié' : '📋 Copier'}
        </button>
      </div>
    </div>
  ) : (
    <div style={{ margin: '10px 0', fontSize: 13, color: '#f6465d' }}>⚠️ Aucune adresse TRC20 enregistrée pour cet utilisateur.</div>
  );

  const handleApproveWithdrawal = async (id) => {
    try {
      await api.post(`/admin/withdrawals/${id}/approve`);
      alert('Demande approuvée!');
      loadWithdrawals();
      loadPendingCounts();
      setSelectedWithdrawal(null);
    } catch (err) {
      alert(err.response?.data?.error || 'Erreur');
    }
  };

  const handlePayWithdrawal = async (id) => {
    try {
      await api.post(`/admin/withdrawals/${id}/pay`, {
        paymentReference: paymentRef,
        paymentMethod: paymentMethod
      });
      alert('Paiement effectué!');
      loadWithdrawals();
      loadPendingCounts();
      setSelectedWithdrawal(null);
      setPaymentRef('');
      setPaymentMethod('');
    } catch (err) {
      alert(err.response?.data?.error || 'Erreur');
    }
  };

  const handleRejectWithdrawal = async (id) => {
    if (!rejectionReason) {
      alert('Veuillez entrer une raison de rejet');
      return;
    }
    try {
      await api.post(`/admin/withdrawals/${id}/reject`, {
        reason: rejectionReason
      });
      alert('Demande rejetée!');
      loadWithdrawals();
      loadPendingCounts();
      setSelectedWithdrawal(null);
      setRejectionReason('');
    } catch (err) {
      alert(err.response?.data?.error || 'Erreur');
    }
  };

  // Helper: parser une valeur JSON si c'est une string (cas MySQL)
  const parseConfigValue = (value) => {
    if (typeof value === 'string') {
      try {
        return JSON.parse(value);
      } catch (e) {
        return value;
      }
    }
    return value;
  };

  const handleConfigEdit = (config) => {
    setEditingConfig(config.key);

    // Parser la valeur si c'est une string JSON (cas MySQL)
    const parsedValue = parseConfigValue(config.value);

    setConfigValue(JSON.stringify(parsedValue, null, 2));

    // Éditeur dédié pour les heures de distribution (formulaire déroulable)
    // Les heures sont stockées en UTC, on les affiche en Paris
    if (config.key === 'distribution_hours' && Array.isArray(parsedValue)) {
      setConfigHours(parsedValue.map(s => {
        const paris = utcToParis(Number(s.hour) || 0, Number(s.minute) || 0);
        return { hour: paris.hour, minute: paris.minute };
      }));
    } else {
      setConfigHours([]);
    }
  };

  // Manipulation des créneaux horaires
  const updateHourSlot = (index, field, value) => {
    setConfigHours(prev => prev.map((s, i) => i === index ? { ...s, [field]: Number(value) } : s));
  };
  const addHourSlot = () => setConfigHours(prev => [...prev, { hour: 16, minute: 0 }]);
  const removeHourSlot = (index) => setConfigHours(prev => prev.filter((_, i) => i !== index));

  const handleConfigSave = async () => {
    try {
      let parsedValue;
      // distribution_hours: convertir Paris → UTC avant envoi
      if (editingConfig === 'distribution_hours') {
        parsedValue = configHours.map(s => {
          const utc = parisToUtc(Number(s.hour), Number(s.minute));
          return { hour: utc.hour, minute: utc.minute };
        });
      } else {
        try {
          parsedValue = JSON.parse(configValue);
        } catch {
          parsedValue = configValue;
        }
      }

      await api.put(`/admin/config/${editingConfig}`, { value: parsedValue });
      await api.post('/admin/config/refresh');
      setEditingConfig(null);
      loadConfigs();
      alert('Configuration mise à jour!');
    } catch (err) {
      alert(err.response?.data?.error || 'Erreur de mise à jour');
    }
  };

  const handleUserSelect = async (userId) => {
    try {
      const response = await api.get(`/admin/users/${userId}`);
      setSelectedUser(response.data);
      setShowTransactions(false);
      setShowDeleteConfirm(false);
      setBlockReason('');
    } catch (err) {
      alert(err.response?.data?.error || 'Erreur');
    }
  };

  // Prépare la transaction et affiche la confirmation
  const handleTransaction = () => {
    if (!selectedUser || !balanceAmount || parseFloat(balanceAmount) <= 0) {
      alert('Veuillez entrer un montant valide');
      return;
    }

    const amount = parseFloat(balanceAmount);
    const currentBalance = parseFloat(selectedUser.user.balance);
    const newBalance = transactionType === 'credit'
      ? currentBalance + amount
      : currentBalance - amount;

    if (transactionType === 'debit' && amount > currentBalance) {
      alert('Solde insuffisant pour ce débit');
      return;
    }

    setPendingTransaction({
      type: transactionType,
      amount: amount,
      reason: transactionReason || (transactionType === 'credit' ? 'Dépôt effectué' : 'Ajustement de solde'),
      user: selectedUser.user,
      currentBalance: currentBalance,
      newBalance: newBalance,
      timestamp: new Date().toLocaleString('fr-FR')
    });
    setShowTransactionConfirm(true);
  };

  // Exécute la transaction après confirmation
  const executeTransaction = async () => {
    if (!pendingTransaction) return;
    try {
      const endpoint = pendingTransaction.type === 'credit'
        ? `/admin/users/${pendingTransaction.user.id}/add-balance`
        : `/admin/users/${pendingTransaction.user.id}/debit-balance`;

      await api.post(endpoint, {
        amount: pendingTransaction.amount,
        reason: pendingTransaction.reason
      });

      setBalanceAmount('');
      setTransactionReason('');
      setShowTransactionConfirm(false);
      setPendingTransaction(null);
      handleUserSelect(pendingTransaction.user.id);
      loadUsers();
    } catch (err) {
      alert(err.response?.data?.error || 'Erreur lors de la transaction');
    }
  };

  const cancelTransaction = () => {
    setShowTransactionConfirm(false);
    setPendingTransaction(null);
  };

  const handleBlockUser = async () => {
    if (!selectedUser) return;
    try {
      await api.post(`/admin/users/${selectedUser.user.id}/block`, {
        reason: blockReason || 'Bloqué par l\'administrateur'
      });
      setBlockReason('');
      handleUserSelect(selectedUser.user.id);
      loadUsers();
      alert('Utilisateur bloqué!');
    } catch (err) {
      alert(err.response?.data?.error || 'Erreur');
    }
  };

  const handleUnblockUser = async () => {
    if (!selectedUser) return;
    try {
      await api.post(`/admin/users/${selectedUser.user.id}/unblock`);
      handleUserSelect(selectedUser.user.id);
      loadUsers();
      alert('Utilisateur débloqué!');
    } catch (err) {
      alert(err.response?.data?.error || 'Erreur');
    }
  };

  const handleDeleteUser = async (keepDeposits = true) => {
    if (!selectedUser) return;
    try {
      const res = await api.delete(`/admin/users/${selectedUser.user.id}`, { data: { keepDeposits } });
      setSelectedUser(null);
      setShowDeleteConfirm(false);
      loadUsers();
      alert(res.data?.message || 'Utilisateur supprimé!');
    } catch (err) {
      alert(err.response?.data?.error || 'Erreur');
    }
  };

  const loadUserTransactions = async () => {
    if (!selectedUser) return;
    try {
      const response = await api.get(`/admin/users/${selectedUser.user.id}/transactions`);
      setUserTransactions(response.data.transactions);
      setShowTransactions(true);
    } catch (err) {
      alert(err.response?.data?.error || 'Erreur');
    }
  };

  const handleUserUpdate = async (field, value) => {
    if (!selectedUser) return;
    try {
      await api.put(`/admin/users/${selectedUser.user.id}`, { [field]: value });
      handleUserSelect(selectedUser.user.id);
      alert('Utilisateur mis à jour!');
    } catch (err) {
      alert(err.response?.data?.error || 'Erreur');
    }
  };

  const handleReassignReferrer = async () => {
    if (!selectedUser || reassignCode.length !== 8) return;
    try {
      setReassignSaving(true);
      const res = await api.put(`/admin/users/${selectedUser.user.id}/referrer`, {
        referralCode: reassignCode
      });
      setReassignCode('');
      handleUserSelect(selectedUser.user.id);
      alert(res.data.message || 'Parrain réattribué');
    } catch (err) {
      alert(err.response?.data?.error || 'Erreur');
    } finally {
      setReassignSaving(false);
    }
  };

  // Créer un nouveau compte admin/manager (master admin uniquement)
  const handleCreateAdmin = async (e) => {
    e.preventDefault();
    setCreateAdminLoading(true);
    setCreateAdminError('');
    try {
      await api.post('/admin/create-admin', newAdminForm);
      setShowCreateAdmin(false);
      setNewAdminForm({ username: '', email: '', password: '', role: 'manager' });
      loadUsers();
      alert('Compte créé avec succès !');
    } catch (err) {
      setCreateAdminError(err.response?.data?.error || 'Erreur');
    } finally {
      setCreateAdminLoading(false);
    }
  };

  const handleSystemAction = async (action) => {
    try {
      setLoading(true);
      await api.post(`/admin/system/${action}`);
      alert('Action effectuée!');
      loadDashboard();
    } catch (err) {
      alert(err.response?.data?.error || 'Erreur');
    } finally {
      setLoading(false);
    }
  };

  const formatNumber = (num) => {
    return new Intl.NumberFormat('fr-FR').format(num || 0);
  };

  const formatCurrency = (num) => usdt(num);

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    try {
      const date = new Date(dateStr);
      if (isNaN(date.getTime())) return 'N/A';
      return date.toLocaleDateString('fr-FR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return 'N/A';
    }
  };

  // "Vu la dernière fois" — relatif si récent, sinon date
  const formatLastSeen = (u) => {
    if (u?.isOnline) return 'En ligne';
    if (!u?.lastSeenAt) return 'Jamais vu';
    const diff = Date.now() - new Date(u.lastSeenAt).getTime();
    const min = Math.floor(diff / 60000);
    if (min < 1) return "À l'instant";
    if (min < 60) return `Il y a ${min} min`;
    const h = Math.floor(min / 60);
    if (h < 24) return `Il y a ${h}h`;
    const d = Math.floor(h / 24);
    if (d < 7) return `Il y a ${d}j`;
    return `Vu le ${formatDate(u.lastSeenAt)}`;
  };

  if (!user || !['admin', 'manager'].includes(user.role)) {
    return (
      <div className="admin-access-denied">
        <h1>Accès Refusé</h1>
        <p>Vous devez être administrateur pour accéder à cette page.</p>
        <button onClick={() => navigate('/dashboard')}>Retour</button>
      </div>
    );
  }

  return (
    <div className="admin-page">
      {/* Header */}
      <header className="admin-header">
        <div className="admin-header-left">
          <h1>Dashboard Admin</h1>
          <span className="admin-badge">{user.role.toUpperCase()}</span>
        </div>
        <div className="admin-header-right">
          <span className="admin-user">{user.username}</span>
          <button className="admin-back-btn" onClick={() => navigate('/dashboard')}>
            Retour à l'app
          </button>
        </div>
      </header>

      {/* Navigation */}
      <nav className="admin-nav">
        <button
          className={`admin-nav-item ${activeTab === 'dashboard' ? 'active' : ''}`}
          onClick={() => handleTabChange('dashboard')}
        >
          {t('admin.dashboard')}
        </button>
        <button
          className={`admin-nav-item ${activeTab === 'config' ? 'active' : ''}`}
          onClick={() => handleTabChange('config')}
        >
          {t('admin.configs')}
        </button>
        <button
          className={`admin-nav-item ${activeTab === 'referrals' ? 'active' : ''}`}
          onClick={() => handleTabChange('referrals')}
        >
          Arbre de parrainage
        </button>
        <button
          className={`admin-nav-item ${activeTab === 'withdrawals' ? 'active' : ''}`}
          onClick={() => handleTabChange('withdrawals')}
        >
          {t('admin.withdrawals')}
          {pendingCounts.withdrawals > 0 && (
            <span className="kyc-nav-badge">{pendingCounts.withdrawals > 99 ? '99+' : pendingCounts.withdrawals}</span>
          )}
        </button>
        <button
          className={`admin-nav-item ${activeTab === 'kyc' ? 'active' : ''}`}
          onClick={() => handleTabChange('kyc')}
        >
          {t('admin.kyc')}
          {pendingCounts.kyc > 0 && <span className="kyc-nav-badge">{pendingCounts.kyc > 99 ? '99+' : pendingCounts.kyc}</span>}
        </button>
        <button
          className={`admin-nav-item ${activeTab === 'deposits' ? 'active' : ''}`}
          onClick={() => handleTabChange('deposits')}
        >
          {t('admin.deposits')}
          {pendingCounts.deposits > 0 && (
            <span className="kyc-nav-badge">{pendingCounts.deposits > 99 ? '99+' : pendingCounts.deposits}</span>
          )}
        </button>
        <button
          className={`admin-nav-item ${activeTab === 'schedule' ? 'active' : ''}`}
          onClick={() => handleTabChange('schedule')}
        >
          {t('admin.schedule')}
        </button>
        <button
          className={`admin-nav-item ${activeTab === 'users' ? 'active' : ''}`}
          onClick={() => { handleTabChange('users'); setNewUsersCount(0); }}
        >
          {t('admin.users')} {newUsersCount > 0 && <span className="users-nav-badge">{newUsersCount}</span>}
        </button>
        <button
          className={`admin-nav-item ${activeTab === 'messages' ? 'active' : ''}`}
          onClick={() => handleTabChange('messages')}
        >
          💬 Messages
          {totalUnreadMessages > 0 && (
            <span className="admin-badge">{totalUnreadMessages > 99 ? '99+' : totalUnreadMessages}</span>
          )}
        </button>
        <button
          className={`admin-nav-item ${activeTab === 'notifications' ? 'active' : ''}`}
          onClick={() => handleTabChange('notifications')}
        >
          🔔 Notifications
        </button>
        <button
          className={`admin-nav-item ${activeTab === 'broadcast' ? 'active' : ''}`}
          onClick={() => handleTabChange('broadcast')}
        >
          📣 Diffusion
        </button>
        <button
          className={`admin-nav-item ${activeTab === 'savings' ? 'active' : ''}`}
          onClick={() => handleTabChange('savings')}
        >
          💰 Épargne
        </button>
        <button
          className={`admin-nav-item ${activeTab === 'system' ? 'active' : ''}`}
          onClick={() => handleTabChange('system')}
        >
          {t('admin.system')}
        </button>
      </nav>

      {/* Content */}
      <main className="admin-content">
        {error && <div className="admin-error">{error}</div>}

        {/* Dashboard Tab */}
        {activeTab === 'dashboard' && (
          <div className="admin-dashboard">
            {loading ? (
              <div className="admin-loading">Chargement...</div>
            ) : stats ? (
              <>
                {/* Stats Cards */}
                <div className="admin-stats-grid">
                  <div className="admin-stat-card">
                    <div className="stat-icon icon-users"><IconUsers /></div>
                    <div className="stat-info">
                      <span className="stat-value">{formatNumber(stats.users.total)}</span>
                      <span className="stat-label">Utilisateurs Total</span>
                    </div>
                    <div className="stat-sub">
                      <span className="stat-highlight">+{stats.users.newToday}</span> aujourd'hui
                    </div>
                  </div>

                  <div className="admin-stat-card">
                    <div className="stat-icon icon-trades"><IconTrendingUp /></div>
                    <div className="stat-info">
                      <span className="stat-value">{formatNumber(stats.trades.total)}</span>
                      <span className="stat-label">Trades Total</span>
                    </div>
                    <div className="stat-sub">
                      <span className="stat-highlight">{stats.trades.successRate}%</span> réussite
                    </div>
                  </div>

                  <div className="admin-stat-card">
                    <div className="stat-icon icon-volume"><IconWallet /></div>
                    <div className="stat-info">
                      <span className="stat-value">{formatCurrency(stats.volume.total)}</span>
                      <span className="stat-label">Volume Total</span>
                    </div>
                    <div className="stat-sub">
                      Net: <span className={stats.volume.net >= 0 ? 'positive' : 'negative'}>
                        {formatCurrency(stats.volume.net)}
                      </span>
                    </div>
                  </div>

                  <div className="admin-stat-card">
                    <div className="stat-icon icon-pending"><IconClock /></div>
                    <div className="stat-info">
                      <span className="stat-value">{formatNumber(stats.trades.pending)}</span>
                      <span className="stat-label">En Attente</span>
                    </div>
                    <div className="stat-sub">
                      <span className="stat-highlight">{stats.trades.today}</span> trades aujourd'hui
                    </div>
                  </div>
                </div>

                {/* Ranks Distribution */}
                <div className="admin-section">
                  <h2>Répartition par Rang</h2>
                  <div className="rank-distribution">
                    {Object.entries(stats.usersByRank || {}).map(([rank, count]) => (
                      <div key={rank} className={`rank-bar rank-${rank}`}>
                        <span className="rank-icon">
                          {rank === 'bronze' && '🥉'}
                          {rank === 'silver' && '🥈'}
                          {rank === 'gold' && '🥇'}
                          {rank === 'platinum' && '💎'}
                          {rank === 'diamond' && '👑'}
                        </span>
                        <span className="rank-name">{rank}</span>
                        <span className="rank-count">{count}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </>
            ) : null}
          </div>
        )}

        {/* Config Tab */}
        {activeTab === 'config' && (
          <div className="admin-config">
            <div className="config-header-section">
              <h2>{t('admin.configs')}</h2>
              <p className="config-subtitle">Paramètres dynamiques de l'application</p>
            </div>

            {loading ? (
              <div className="admin-loading">{t('common.loading')}</div>
            ) : (
              <div className="config-grid">
                {Object.entries(configs).map(([category, items]) => (
                  <div key={category} className="config-category-card">
                    <div className="config-category-header">
                      <span className="config-category-icon">
                        {category === 'growth' && '📈'}
                        {category === 'trading' && '💹'}
                        {category === 'distribution' && '🕐'}
                        {category === 'bot' && '🤖'}
                        {category === 'crypto' && '₿'}
                        {category === 'referral' && '🎁'}
                        {category === 'app' && '📱'}
                      </span>
                      <h3 className="config-category-title">
                        {category === 'growth' && 'Croissance'}
                        {category === 'trading' && 'Trading'}
                        {category === 'distribution' && 'Distribution'}
                        {category === 'bot' && 'Bot IA'}
                        {category === 'crypto' && 'Cryptos'}
                        {category === 'referral' && 'Parrainage'}
                        {category === 'app' && 'Application'}
                      </h3>
                      <span className="config-count">{items.length}</span>
                    </div>

                    <div className="config-items">
                      {items.map((config) => (
                        <div key={config.key} className={`config-item ${editingConfig === config.key ? 'editing' : ''}`}>
                          <div className="config-item-header">
                            <div className="config-key-wrap">
                              <span className="config-key">{config.key.replace(/_/g, ' ')}</span>
                              {config.description && (
                                <span className="config-desc" title={config.description}>ℹ️</span>
                              )}
                            </div>
                            <span className={`config-type ${typeof config.value === 'object' ? 'complex' : 'simple'}`}>
                              {typeof config.value === 'object' ? (Array.isArray(config.value) ? 'array' : 'object') : typeof config.value}
                            </span>
                          </div>

                          {editingConfig === config.key ? (
                            <div className="config-edit">
                              {config.key === 'message_expiry_days' && (
                                <p className="config-warning">
                                  ⚠️ Nombre de jours avant <strong>suppression automatique et définitive</strong> d'une
                                  conversation (à partir du dernier message admin). Une valeur plus basse effacera
                                  plus tôt les conversations existantes.
                                </p>
                              )}
                              {config.key === 'cycle_length_days' && (() => {
                                const W = parseInt(configValue, 10);
                                if (!Number.isInteger(W) || W < 2) return null;
                                let P = 1; for (let p = 8; p >= 2; p--) { if (W % p === 0) { P = p; break; } }
                                const suggested = [28, 30, 35, 40, 45, 60];
                                return (
                                  <p className="config-warning">
                                    {P === 1 ? (
                                      <>⚠️ <strong>{W}</strong> n'a aucun bon diviseur (nombre premier) → le cycle deviendra un
                                      <strong> gain quotidien constant, sans jours rouges</strong>. Le doublement reste exact
                                      mais le planning perd sa variété. Valeurs conseillées : {suggested.join(', ')}.</>
                                    ) : (
                                      <>✓ Cycle de <strong>{W} jours</strong> → motif de période <strong>P={P}</strong>
                                      (invariance exacte : ×cible pour toute date d'entrée). Régénère le planning après changement.</>
                                    )}
                                  </p>
                                );
                              })()}
                              {config.key === 'distribution_hours' ? (
                                <div className="config-hours-editor">
                                  <p className="config-hours-hint" style={{ fontSize: '0.8rem', color: '#848e9c', margin: '0 0 8px' }}>
                                    Heures Paris · Créneaux 1-3 = base (tous les rangs) · 4+ = bonus
                                  </p>
                                  {configHours.map((slot, idx) => (
                                    <div key={idx} style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 6 }}>
                                      <span style={{ width: 80, fontSize: '0.8rem', color: '#848e9c' }}>
                                        {idx < 3 ? `Base ${idx + 1}` : `Bonus ${idx - 2}`}
                                      </span>
                                      <select value={slot.hour} onChange={(e) => updateHourSlot(idx, 'hour', e.target.value)} style={{ padding: '4px 8px', borderRadius: 4 }}>
                                        {Array.from({ length: 24 }, (_, h) => (
                                          <option key={h} value={h}>{String(h).padStart(2, '0')}h</option>
                                        ))}
                                      </select>
                                      <span>:</span>
                                      <select value={slot.minute} onChange={(e) => updateHourSlot(idx, 'minute', e.target.value)} style={{ padding: '4px 8px', borderRadius: 4 }}>
                                        {Array.from({ length: 60 }, (_, m) => (
                                          <option key={m} value={m}>{String(m).padStart(2, '0')}</option>
                                        ))}
                                      </select>
                                      <button onClick={() => removeHourSlot(idx)} title="Supprimer ce créneau" style={{ background: 'transparent', border: 'none', cursor: 'pointer', fontSize: '1rem' }}>🗑️</button>
                                    </div>
                                  ))}
                                  <button onClick={addHourSlot} style={{ marginTop: 6, padding: '6px 12px', borderRadius: 6, cursor: 'pointer' }}>
                                    + Ajouter un créneau
                                  </button>
                                </div>
                              ) : (
                                <textarea
                                  value={configValue}
                                  onChange={(e) => setConfigValue(e.target.value)}
                                  rows={typeof config.value === 'object' ? 12 : 3}
                                  className="config-textarea"
                                  spellCheck={false}
                                />
                              )}
                              <div className="config-edit-actions">
                                <button className="btn-config-save" onClick={handleConfigSave}>
                                  ✓ {t('admin.save')}
                                </button>
                                <button className="btn-config-cancel" onClick={() => setEditingConfig(null)}>
                                  ✕ {t('admin.cancel')}
                                </button>
                              </div>
                            </div>
                          ) : (
                            <div className="config-view">
                              {(() => {
                                const parsed = parseConfigValue(config.value);
                                if (typeof parsed === 'object' && parsed !== null) {
                                  return (
                                    <div className="config-value-complex">
                                      {Array.isArray(parsed) ? (
                                        <span className="config-array-preview">
                                          [{parsed.length} éléments]
                                        </span>
                                      ) : (
                                        <div className="config-object-preview">
                                          {Object.entries(parsed).slice(0, 4).map(([k, v]) => (
                                            <div key={k} className="config-obj-row">
                                              <span className="obj-key">{k}:</span>
                                              <span className="obj-value">{JSON.stringify(v)}</span>
                                            </div>
                                          ))}
                                          {Object.keys(parsed).length > 4 && (
                                            <span className="config-more">+{Object.keys(parsed).length - 4} autres...</span>
                                          )}
                                        </div>
                                      )}
                                    </div>
                                  );
                                }
                                return (
                                  <span className={`config-value-simple ${typeof parsed}`}>
                                    {String(parsed)}
                                  </span>
                                );
                              })()}
                              <button
                                className="btn-config-edit"
                                onClick={() => handleConfigEdit(config)}
                              >
                                ✏️
                              </button>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Users Tab */}
        {activeTab === 'users' && (
          <div className="admin-users">
            <div className="users-search">
              <input
                type="text"
                placeholder="Rechercher un utilisateur..."
                value={userSearch}
                onChange={(e) => setUserSearch(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && loadUsers()}
              />
              <button onClick={loadUsers}>Rechercher</button>
            </div>

            {/* Création admin/manager — réservé au master admin */}
            {user?.isMasterAdmin && (
              <div className="wd-section">
                <button
                  className="btn-approve"
                  onClick={() => setShowCreateAdmin(v => !v)}
                >
                  {showCreateAdmin ? '✕ Annuler' : '+ Nouveau compte admin/manager'}
                </button>

                {showCreateAdmin && (
                  <form className="create-admin-form" onSubmit={handleCreateAdmin}>
                    <input
                      type="text"
                      placeholder="Nom d'utilisateur"
                      value={newAdminForm.username}
                      onChange={(e) => setNewAdminForm(f => ({ ...f, username: e.target.value }))}
                      required
                    />
                    <input
                      type="email"
                      placeholder="Email"
                      value={newAdminForm.email}
                      onChange={(e) => setNewAdminForm(f => ({ ...f, email: e.target.value }))}
                      required
                    />
                    <input
                      type="password"
                      placeholder="Mot de passe"
                      value={newAdminForm.password}
                      onChange={(e) => setNewAdminForm(f => ({ ...f, password: e.target.value }))}
                      required
                    />
                    <select
                      value={newAdminForm.role}
                      onChange={(e) => setNewAdminForm(f => ({ ...f, role: e.target.value }))}
                    >
                      <option value="manager">Manager</option>
                      <option value="admin">Admin</option>
                    </select>
                    {createAdminError && <div className="deposit-decode-error">{createAdminError}</div>}
                    <button type="submit" className="btn-approve" disabled={createAdminLoading}>
                      {createAdminLoading ? 'Création...' : 'Créer le compte'}
                    </button>
                  </form>
                )}
              </div>
            )}

            <div className="users-sort">
              <label>Trier par :</label>
              <select value={userSortBy} onChange={(e) => setUserSortBy(e.target.value)}>
                <option value="createdAt">Date d'inscription</option>
                <option value="capitalConfirmedAt">Date d'investissement</option>
                <option value="lastSeenAt">Dernière activité</option>
                <option value="balance">Solde</option>
                <option value="rank">Rang</option>
              </select>
              <button
                className="sort-order-btn"
                onClick={() => setUserSortOrder(prev => prev === 'desc' ? 'asc' : 'desc')}
                title={userSortOrder === 'desc' ? 'Décroissant' : 'Croissant'}
              >
                {userSortOrder === 'desc' ? '↓' : '↑'}
              </button>
              <select
                className="users-presence-filter"
                value={userPresence}
                onChange={(e) => setUserPresence(e.target.value)}
                title="Filtrer par présence"
              >
                <option value="">Tous</option>
                <option value="online">🟢 En ligne</option>
                <option value="active24h">Actifs (24h)</option>
                <option value="active7d">Actifs (7j)</option>
                <option value="inactive7d">Inactifs +7j</option>
                <option value="inactive30d">Inactifs +30j</option>
              </select>
            </div>

            <div className="users-layout">
              {/* Users List */}
              <div className="users-list">
                {loading ? (
                  <div className="admin-loading">Chargement...</div>
                ) : (
                  sortedUsers.map((u) => (
                    <div
                      key={u.id}
                      className={`user-item ${selectedUser?.user?.id === u.id ? 'selected' : ''} ${u.isBlocked ? 'blocked' : ''}`}
                      onClick={() => handleUserSelect(u.id)}
                    >
                      <div className="user-avatar">
                        {u.username.charAt(0).toUpperCase()}
                        {u.isOnline && <span className="online-dot" title="En ligne"></span>}
                      </div>
                      <div className="user-info">
                        <span className="user-name">
                          {u.username}
                          {u.isBlocked && <span className="blocked-badge">BLOQUE</span>}
                        </span>
                        <span className="user-email">{u.email}</span>
                        <span className={`user-lastseen ${u.isOnline ? 'online' : ''}`}>{formatLastSeen(u)}</span>
                      </div>
                      <div className="user-meta">
                        <span className={`user-rank rank-${u.rank}`}>{u.rank}</span>
                        <span className="user-balance">{formatCurrency(u.balance)}</span>
                      </div>
                    </div>
                  ))
                )}
              </div>

              {/* User Detail */}
              {selectedUser && (
                <div className="user-detail">
                  <div className="user-detail-header">
                    <h3>{selectedUser.user.username}</h3>
                    <div className="user-status-badges">
                      {selectedUser.user.capitalConfirmed ? (
                        <span className="status-badge confirmed">Capital confirmé</span>
                      ) : (
                        <span className="status-badge pending">En attente</span>
                      )}
                      {selectedUser.user.isBlocked && (
                        <span className="status-badge blocked">BLOQUÉ</span>
                      )}
                    </div>
                  </div>

                  <div className="user-quick-stats">
                    <div className="quick-stat">
                      <span className="qs-value">{formatCurrency(selectedUser.user.balance)}</span>
                      <span className="qs-label">Solde</span>
                    </div>
                    <div className="quick-stat">
                      <span className="qs-value">{selectedUser.stats.completedTrades}</span>
                      <span className="qs-label">Trades</span>
                    </div>
                    <div className="quick-stat">
                      <span className="qs-value">{selectedUser.stats.successRate}%</span>
                      <span className="qs-label">Réussite</span>
                    </div>
                  </div>

                  {/* Accordéon Infos */}
                  <div className={`accordion ${openAccordions.info ? 'open' : ''}`}>
                    <div className="accordion-header" onClick={() => setOpenAccordions(p => ({ ...p, info: !p.info }))}>
                      <span>Informations</span>
                      <span className="accordion-arrow">{openAccordions.info ? '▼' : '▶'}</span>
                    </div>
                    {openAccordions.info && (
                      <div className="accordion-body">
                        <div className="detail-row compact">
                          <span>Email</span>
                          <span>{selectedUser.user.email}</span>
                        </div>
                        <div className="detail-row compact">
                          <span>ID</span>
                          <span className="id-value">{selectedUser.user.id.slice(0, 8)}...</span>
                        </div>
                        <div className="detail-row compact">
                          <span>Rang</span>
                          <select value={selectedUser.user.rank} onChange={(e) => handleUserUpdate('rank', e.target.value)}>
                            <option value="bronze">Bronze</option>
                            <option value="silver">Silver</option>
                            <option value="gold">Gold</option>
                            <option value="platinum">Platinum</option>
                            <option value="diamond">Diamond</option>
                          </select>
                        </div>
                        <div className="detail-row compact">
                          <span>Rôle</span>
                          {user?.isMasterAdmin && !selectedUser.user.isMasterAdmin ? (
                            <select value={selectedUser.user.role} onChange={(e) => handleUserUpdate('role', e.target.value)}>
                              <option value="user">User</option>
                              <option value="manager">Manager</option>
                              <option value="admin">Admin</option>
                            </select>
                          ) : (
                            <span>
                              {selectedUser.user.role}
                              {selectedUser.user.isMasterAdmin && ' 👑'}
                            </span>
                          )}
                        </div>
                        <div className="detail-row compact">
                          <span>Alias public (parrain)</span>
                          <input
                            type="text"
                            defaultValue={selectedUser.user.publicAlias || ''}
                            placeholder="ex: Coach Ahmed (anonymise l'email)"
                            title="Nom affiché à un filleul à la place du pseudo/email réel de ce compte. Renseigné = email masqué au filleul."
                            onBlur={(e) => {
                              if (e.target.value !== (selectedUser.user.publicAlias || '')) {
                                handleUserUpdate('publicAlias', e.target.value);
                              }
                            }}
                          />
                        </div>
                        <div className="detail-row compact">
                          <span>Inscrit</span>
                          <span>{formatDate(selectedUser.user.createdAt)}</span>
                        </div>
                        <div className="detail-row compact">
                          <span>Présence</span>
                          <span className={selectedUser.user.isOnline ? 'presence-online' : ''}>
                            {selectedUser.user.isOnline ? '🟢 En ligne' : formatLastSeen(selectedUser.user)}
                          </span>
                        </div>
                        {selectedUser.user.isBlocked && selectedUser.user.blockedReason && (
                          <div className="detail-row compact blocked-info">
                            <span>Raison blocage</span>
                            <span>{selectedUser.user.blockedReason}</span>
                          </div>
                        )}
                      </div>
                    )}
                  </div>

                  {/* Accordéon Investissement */}
                  <div className={`accordion ${openAccordions.invest ? 'open' : ''}`}>
                    <div className="accordion-header" onClick={() => setOpenAccordions(p => ({ ...p, invest: !p.invest }))}>
                      <span>Investissement</span>
                      <span className="accordion-arrow">{openAccordions.invest ? '▼' : '▶'}</span>
                    </div>
                    {openAccordions.invest && (
                      <div className="accordion-body">
                        <div className="detail-row compact">
                          <span>Capital confirmé</span>
                          <span className={selectedUser.user.capitalConfirmed ? 'text-green' : 'text-orange'}>
                            {selectedUser.user.capitalConfirmed ? 'Oui' : 'Non'}
                          </span>
                        </div>
                        {selectedUser.user.capitalConfirmedAt && (
                          <div className="detail-row compact">
                            <span>Date confirmation</span>
                            <span>{formatDate(selectedUser.user.capitalConfirmedAt)}</span>
                          </div>
                        )}
                        {selectedUser.investment && (
                          <div className="detail-row compact">
                            <span>Premier dépôt</span>
                            <span>{formatCurrency(selectedUser.investment.amount)} le {formatDate(selectedUser.investment.date)}</span>
                          </div>
                        )}
                        <div className="detail-row compact">
                          <span>Volume total</span>
                          <span>{formatCurrency(selectedUser.stats.totalVolume)}</span>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Accordéon Parrainage */}
                  <div className={`accordion ${openAccordions.referral ? 'open' : ''}`}>
                    <div className="accordion-header" onClick={() => setOpenAccordions(p => ({ ...p, referral: !p.referral }))}>
                      <span>Parrainage ({selectedUser.user.referrals?.length || 0})</span>
                      <span className="accordion-arrow">{openAccordions.referral ? '▼' : '▶'}</span>
                    </div>
                    {openAccordions.referral && (
                      <div className="accordion-body">
                        <div className="detail-row compact">
                          <span>Parrainé par</span>
                          <span>{selectedUser.user.referrer?.username || '—'}</span>
                        </div>
                        <div className="detail-row compact">
                          <span>Bonus en attente</span>
                          <span>{formatCurrency(selectedUser.user.pendingReferralBonus || 0)}</span>
                        </div>
                        {/* Réattribuer le parrain (corriger une erreur) */}
                        <div className="reassign-referrer">
                          <label>Réattribuer le parrain</label>
                          <div className="reassign-row">
                            <input
                              type="text"
                              value={reassignCode}
                              onChange={(e) => setReassignCode(e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 8))}
                              placeholder="Code du nouveau parrain (8 car.)"
                              maxLength={8}
                            />
                            <button
                              onClick={handleReassignReferrer}
                              disabled={reassignCode.length !== 8 || reassignSaving}
                            >
                              {reassignSaving ? '...' : 'Changer'}
                            </button>
                          </div>
                          {selectedUser.user.capitalConfirmed && (
                            <span className="reassign-warn">
                              ⚠️ Ce filleul a déjà confirmé son capital — réattribution bloquée (bonus déjà versé).
                            </span>
                          )}
                        </div>
                        {selectedUser.user.referrals?.length > 0 && (
                          <div className="referrals-list">
                            <span className="referrals-title">Ses filleuls:</span>
                            {selectedUser.user.referrals.map(r => (
                              <div key={r.id} className="referral-item">
                                <span>{r.username}</span>
                                <span className={r.capitalConfirmed ? 'text-green' : 'text-gray'}>
                                  {r.capitalConfirmed ? formatCurrency(r.balance) : 'Non confirmé'}
                                </span>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    )}
                  </div>

                  {/* Transaction form (toujours visible) */}
                  <div className="user-detail-section compact">
                    <h4>Transaction</h4>
                    <div className="transaction-form">
                      <div className="transaction-type-selector">
                        <button
                          className={`type-btn ${transactionType === 'credit' ? 'active credit' : ''}`}
                          onClick={() => setTransactionType('credit')}
                        >
                          + Crédit
                        </button>
                        <button
                          className={`type-btn ${transactionType === 'debit' ? 'active debit' : ''}`}
                          onClick={() => setTransactionType('debit')}
                        >
                          - Débit
                        </button>
                      </div>
                      <input
                        type="number"
                        placeholder="Montant ($)"
                        value={balanceAmount}
                        onChange={(e) => setBalanceAmount(e.target.value)}
                        min="0"
                        step="0.01"
                      />
                      <input
                        type="text"
                        placeholder="Libellé / Raison"
                        value={transactionReason}
                        onChange={(e) => setTransactionReason(e.target.value)}
                      />
                      <button
                        className={`btn-transaction ${transactionType}`}
                        onClick={handleTransaction}
                      >
                        {transactionType === 'credit' ? 'Ajouter' : 'Retirer'}
                      </button>
                    </div>
                    <button className="btn-show-history" onClick={loadUserTransactions}>
                      Voir l'historique des transactions
                    </button>
                  </div>

                  {showTransactions && (
                    <div className="user-detail-section transactions-section">
                      <h4>Historique des Transactions</h4>
                      <div className="transactions-list">
                        {userTransactions.length === 0 ? (
                          <p className="no-data">Aucune transaction</p>
                        ) : (
                          userTransactions.map((tx) => (
                            <div key={tx.id} className={`transaction-item ${parseFloat(tx.amount) >= 0 ? 'credit' : 'debit'}`}>
                              <div className="tx-info">
                                <span className="tx-type">{tx.type}</span>
                                <span className="tx-desc">{tx.description || '-'}</span>
                                <span className="tx-date">{formatDate(tx.createdAt)}</span>
                              </div>
                              <span className={`tx-amount ${parseFloat(tx.amount) >= 0 ? 'positive' : 'negative'}`}>
                                {parseFloat(tx.amount) >= 0 ? '+' : ''}{formatCurrency(tx.amount)}
                              </span>
                            </div>
                          ))
                        )}
                      </div>
                    </div>
                  )}

                  {/* Accordéon Actions */}
                  <div className={`accordion ${openAccordions.actions ? 'open' : ''}`}>
                    <div className="accordion-header" onClick={() => setOpenAccordions(p => ({ ...p, actions: !p.actions }))}>
                      <span>Actions</span>
                      <span className="accordion-arrow">{openAccordions.actions ? '▼' : '▶'}</span>
                    </div>
                    {openAccordions.actions && (
                      <div className="accordion-body">
                        {selectedUser.user.isBlocked ? (
                          <button className="btn-unblock btn-small" onClick={handleUnblockUser}>
                            Débloquer
                          </button>
                        ) : (
                          <div className="block-form-compact">
                            <input
                              type="text"
                              placeholder="Raison (optionnel)"
                              value={blockReason}
                              onChange={(e) => setBlockReason(e.target.value)}
                            />
                            <button className="btn-block btn-small" onClick={handleBlockUser}>
                              Bloquer
                            </button>
                          </div>
                        )}

                        <div className="danger-zone-compact">
                          {!showDeleteConfirm ? (
                            <button className="btn-delete btn-small" onClick={() => setShowDeleteConfirm(true)}>
                              Supprimer
                            </button>
                          ) : (
                            <div className="delete-confirm-compact">
                              <span>Supprimer ce compte ? Ses dépôts dans les statistiques :</span>
                              <div className="delete-actions-compact" style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                                <button className="btn-confirm-delete btn-small" onClick={() => handleDeleteUser(true)} title="Le compte est supprimé mais ses dépôts restent comptés dans les statistiques (sous son pseudo figé).">
                                  Conserver les dépôts
                                </button>
                                <button className="btn-delete btn-small" onClick={() => handleDeleteUser(false)} title="Le compte ET ses dépôts sont supprimés — les statistiques ne les compteront plus.">
                                  Retirer des stats
                                </button>
                                <button className="btn-cancel-delete btn-small" onClick={() => setShowDeleteConfirm(false)}>Annuler</button>
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Referral Tree Tab */}
        {activeTab === 'referrals' && (
          <div className="admin-referral-tree-tab">
            <h2>Arbre de parrainage</h2>
            <p className="deposits-desc">
              Parcourez les filleuls sur plusieurs générations. Cliquez sur un nœud pour le déplier, ou recherchez directement un utilisateur.
            </p>
            <ReferralTree />
          </div>
        )}

        {/* Withdrawals Tab */}
        {activeTab === 'withdrawals' && (
          <div className="admin-withdrawals">
            <h2>Gestion des Retraits</h2>

            {withdrawalsLoading ? (
              <div className="loading-state">Chargement...</div>
            ) : (
              <>
                {/* Dashboard Stats */}
                {withdrawalsDashboard && (
                  <div className="withdrawals-dashboard">
                    <div className="wd-stats-grid">
                      <div className="wd-stat-card total">
                        <span className="wd-stat-icon icon-volume"><IconWallet /></span>
                        <div className="wd-stat-content">
                          <span className="wd-stat-value">{formatCurrency(withdrawalsDashboard.totalPlatformBalance)}</span>
                          <span className="wd-stat-label">Solde Total Plateforme</span>
                        </div>
                      </div>
                      <div className="wd-stat-card pending">
                        <span className="wd-stat-icon icon-pending"><IconClock /></span>
                        <div className="wd-stat-content">
                          <span className="wd-stat-value">{formatCurrency(withdrawalsDashboard.pendingWithdrawals.totalAmount)}</span>
                          <span className="wd-stat-label">{withdrawalsDashboard.pendingWithdrawals.count} demandes en attente</span>
                        </div>
                      </div>
                      <div className="wd-stat-card blocked">
                        <span className="wd-stat-icon icon-blocked"><IconLock /></span>
                        <div className="wd-stat-content">
                          <span className="wd-stat-value">{formatCurrency(withdrawalsDashboard.approvedWithdrawals.blockedAmount)}</span>
                          <span className="wd-stat-label">{withdrawalsDashboard.approvedWithdrawals.count} approuvés (bloqués)</span>
                        </div>
                      </div>
                      <div className="wd-stat-card fees">
                        <span className="wd-stat-icon icon-fees"><IconChart /></span>
                        <div className="wd-stat-content">
                          <span className="wd-stat-value">{formatCurrency(withdrawalsDashboard.currentMonth.feesCollected)}</span>
                          <span className="wd-stat-label">Frais collectés ce mois</span>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Adresses de retrait TRC20 en attente de validation */}
                <div className="wd-section">
                  <h3>
                    Adresses de retrait à valider
                    {addressPending.length > 0 && (
                      <span className="binance-pending-count">{addressPending.length}</span>
                    )}
                  </h3>
                  {addressPending.length === 0 ? (
                    <p className="no-data">Aucune adresse en attente</p>
                  ) : (
                    <div className="binance-admin-list">
                      {addressPending.map((u) => (
                        <div key={u.id} className="binance-admin-item">
                          <div className="binance-admin-user">
                            <span className="wd-username">{u.username}</span>
                            <span className="wd-email">{u.email}</span>
                            <span className={`kyc-mini ${u.kycStatus}`}>KYC: {u.kycStatus}</span>
                          </div>
                          <div className="binance-admin-address">
                            <span className="binance-network-chip">TRC20</span>
                            <span className="binance-address-value">{u.address}</span>
                          </div>
                          {addressRejectingId === u.id ? (
                            <div className="binance-admin-reject">
                              <input
                                type="text"
                                placeholder="Raison du rejet"
                                value={addressRejectReason}
                                onChange={(e) => setAddressRejectReason(e.target.value)}
                              />
                              <button
                                className="btn-reject"
                                disabled={addressActionLoading === u.id}
                                onClick={() => handleWithdrawalAddress(u.id, 'reject', addressRejectReason)}
                              >
                                Confirmer le rejet
                              </button>
                              <button className="btn-cancel-slot" onClick={() => setAddressRejectingId(null)}>
                                Annuler
                              </button>
                            </div>
                          ) : (
                            <div className="binance-admin-actions">
                              <button
                                className="btn-approve"
                                disabled={addressActionLoading === u.id}
                                onClick={() => handleWithdrawalAddress(u.id, 'approve')}
                              >
                                ✓ Valider (verrouille l'adresse)
                              </button>
                              <button
                                className="btn-reject"
                                disabled={addressActionLoading === u.id}
                                onClick={() => setAddressRejectingId(u.id)}
                              >
                                ✕ Rejeter
                              </button>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Pending Withdrawals */}
                <div className="wd-section">
                  <h3>Demandes en Attente</h3>
                  {withdrawalsDashboard?.pendingWithdrawals?.requests?.length > 0 ? (
                    <div className="wd-table">
                      <div className="wd-table-header">
                        <span>Utilisateur</span>
                        <span>Montant</span>
                        <span>Frais</span>
                        <span>Net</span>
                        <span>Date</span>
                        <span>Actions</span>
                      </div>
                      {withdrawalsDashboard.pendingWithdrawals.requests.map((w) => (
                        <div key={w.id} className="wd-table-row">
                          <div className="wd-user-info">
                            <span className="wd-username">{w.username}</span>
                            <span className="wd-email">{w.email}</span>
                          </div>
                          <span className="wd-amount">{formatCurrency(w.amount)}</span>
                          <span className="wd-fee">-{formatCurrency(w.fee)}</span>
                          <span className="wd-net">{formatCurrency(w.netAmount)}</span>
                          <span className="wd-date">{formatDate(w.requestedAt)}</span>
                          <div className="wd-actions">
                            <button
                              className="wd-btn approve"
                              onClick={() => setSelectedWithdrawal({ ...w, action: 'approve' })}
                            >
                              Approuver
                            </button>
                            <button
                              className="wd-btn reject"
                              onClick={() => setSelectedWithdrawal({ ...w, action: 'reject' })}
                            >
                              Rejeter
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="no-data">Aucune demande en attente</p>
                  )}
                </div>

                {/* Approved Withdrawals (to pay) */}
                <div className="wd-section">
                  <h3>Retraits Approuvés (à payer)</h3>
                  {withdrawalsDashboard?.approvedWithdrawals?.requests?.length > 0 ? (
                    <div className="wd-table">
                      <div className="wd-table-header">
                        <span>Utilisateur</span>
                        <span>Montant Net</span>
                        <span>Date demande</span>
                        <span>Actions</span>
                      </div>
                      {withdrawalsDashboard.approvedWithdrawals.requests.map((w) => (
                        <div key={w.id} className="wd-table-row approved">
                          <div className="wd-user-info">
                            <span className="wd-username">{w.username}</span>
                            <span className="wd-email">{w.email}</span>
                          </div>
                          <span className="wd-net highlight">{formatCurrency(w.netAmount)}</span>
                          <span className="wd-date">{formatDate(w.requestedAt)}</span>
                          <div className="wd-actions">
                            <button
                              className="wd-btn pay"
                              onClick={() => setSelectedWithdrawal({ ...w, action: 'pay' })}
                            >
                              Marquer Payé
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="no-data">Aucun retrait approuvé</p>
                  )}
                </div>

                {/* All Withdrawals History */}
                <div className="wd-section">
                  <h3>Historique Complet</h3>
                  {withdrawalsList.length > 0 ? (
                    <div className="wd-table">
                      <div className="wd-table-header">
                        <span>Utilisateur</span>
                        <span>Montant</span>
                        <span>Statut</span>
                        <span>Date</span>
                        <span>Référence</span>
                      </div>
                      {withdrawalsList.map((w) => (
                        <div key={w.id} className={`wd-table-row ${w.status}`}>
                          <div className="wd-user-info">
                            <span className="wd-username">{w.username}</span>
                          </div>
                          <span className="wd-net">{formatCurrency(w.netAmount)}</span>
                          <span className={`wd-status ${w.status}`}>
                            {w.status === 'pending' && '⏳ En attente'}
                            {w.status === 'approved' && '✅ Approuvé'}
                            {w.status === 'paid' && '💰 Payé'}
                            {w.status === 'rejected' && '❌ Rejeté'}
                            {w.status === 'cancelled' && '🚫 Annulé'}
                          </span>
                          <span className="wd-date">{formatDate(w.requestedAt)}</span>
                          <span className="wd-ref">{w.paymentReference || '-'}</span>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="no-data">Aucun historique</p>
                  )}
                </div>

                {/* Payment Modal */}
                {selectedWithdrawal && selectedWithdrawal.action === 'approve' && (
                  <div className="modal-overlay">
                    <div className="modal-content wd-modal">
                      <h3>Approuver le retrait</h3>
                      <div className="wd-modal-info">
                        <p><strong>Utilisateur :</strong> {selectedWithdrawal.username}</p>
                        <p><strong>Montant net à payer :</strong> {formatCurrency(selectedWithdrawal.netAmount)}</p>
                        {selectedWithdrawal.source === 'savings' && <p><strong>Source :</strong> Portefeuille épargne</p>}
                      </div>
                      <AddressCopyBlock address={selectedWithdrawal.withdrawalAddress} />
                      <div className="wd-modal-actions">
                        <button className="wd-btn approve" onClick={() => handleApproveWithdrawal(selectedWithdrawal.id)}>
                          Confirmer l'approbation
                        </button>
                        <button className="wd-btn cancel" onClick={() => setSelectedWithdrawal(null)}>
                          Annuler
                        </button>
                      </div>
                    </div>
                  </div>
                )}

                {selectedWithdrawal && selectedWithdrawal.action === 'pay' && (
                  <div className="modal-overlay">
                    <div className="modal-content wd-modal">
                      <h3>Confirmer le Paiement</h3>
                      <div className="wd-modal-info">
                        <p><strong>Utilisateur:</strong> {selectedWithdrawal.username}</p>
                        <p><strong>Montant à payer:</strong> {formatCurrency(selectedWithdrawal.netAmount)}</p>
                      </div>
                      <AddressCopyBlock address={selectedWithdrawal.withdrawalAddress} />
                      <div className="wd-modal-form">
                        <input
                          type="text"
                          placeholder="Référence du paiement"
                          value={paymentRef}
                          onChange={(e) => setPaymentRef(e.target.value)}
                        />
                        <select
                          value={paymentMethod}
                          onChange={(e) => setPaymentMethod(e.target.value)}
                        >
                          <option value="">Méthode de paiement</option>
                          <option value="virement">Virement bancaire</option>
                          <option value="mobile_money">Mobile Money</option>
                          <option value="crypto">Crypto</option>
                          <option value="autre">Autre</option>
                        </select>
                      </div>
                      <div className="wd-modal-actions">
                        <button
                          className="wd-btn pay"
                          onClick={() => handlePayWithdrawal(selectedWithdrawal.id)}
                        >
                          Confirmer le Paiement
                        </button>
                        <button
                          className="wd-btn cancel"
                          onClick={() => {
                            setSelectedWithdrawal(null);
                            setPaymentRef('');
                            setPaymentMethod('');
                          }}
                        >
                          Annuler
                        </button>
                      </div>
                    </div>
                  </div>
                )}

                {/* Rejection Modal */}
                {selectedWithdrawal && selectedWithdrawal.action === 'reject' && (
                  <div className="modal-overlay">
                    <div className="modal-content wd-modal">
                      <h3>Rejeter la Demande</h3>
                      <div className="wd-modal-info">
                        <p><strong>Utilisateur:</strong> {selectedWithdrawal.username}</p>
                        <p><strong>Montant demandé:</strong> {formatCurrency(selectedWithdrawal.amount)}</p>
                      </div>
                      <div className="wd-modal-form">
                        <textarea
                          placeholder="Raison du rejet (obligatoire)"
                          value={rejectionReason}
                          onChange={(e) => setRejectionReason(e.target.value)}
                          rows={3}
                        />
                      </div>
                      <div className="wd-modal-actions">
                        <button
                          className="wd-btn reject"
                          onClick={() => handleRejectWithdrawal(selectedWithdrawal.id)}
                        >
                          Confirmer le Rejet
                        </button>
                        <button
                          className="wd-btn cancel"
                          onClick={() => {
                            setSelectedWithdrawal(null);
                            setRejectionReason('');
                          }}
                        >
                          Annuler
                        </button>
                      </div>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        )}

        {/* Schedule Tab */}
        {activeTab === 'schedule' && (
          <div className="admin-schedule">
            <h2>{t('admin.monthlySchedule')}</h2>

            {/* Sélecteur de mois + génération 12 mois */}
            <div className="schedule-month-selector" style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', margin: '0 0 16px' }}>
              <label htmlFor="scheduleMonth" style={{ fontWeight: 600 }}>📅 Mois :</label>
              <select
                id="scheduleMonth"
                value={scheduleMonth}
                onChange={(e) => { setScheduleMonth(e.target.value); loadSchedule(e.target.value); }}
                style={{ padding: '6px 10px', borderRadius: 6 }}
              >
                {(scheduleMonths.length ? scheduleMonths.map(m => m.monthYear) : Array.from({ length: 13 }, (_, i) => {
                  const base = new Date();
                  const d = new Date(base.getFullYear(), base.getMonth() + i, 1);
                  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
                })).map(value => {
                  const info = scheduleMonths.find(m => m.monthYear === value);
                  const [yy, mm] = value.split('-').map(Number);
                  const d = new Date(yy, mm - 1, 1);
                  const status = info?.status;
                  const suffix = status === 'current' ? ' — ● EN COURS'
                    : status === 'past' ? ' (passé)'
                    : status === 'future' ? ' (à venir)' : '';
                  const del = info?.deletionScheduledAt ? ` 🗑️ suppr. J-${info.daysLeft}` : '';
                  const label = d.toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' }) + suffix + del;
                  return <option key={value} value={value}>{label}</option>;
                })}
              </select>
              <button
                className="schedule-regenerate-btn"
                onClick={handleGenerateMonths}
                disabled={generatingMonths}
                title="Génère le mois courant + 12 mois suivants. Les mois passés et les slots distribués sont protégés."
              >
                {generatingMonths ? '⏳ Génération…' : '📅 Générer 12 mois'}
              </button>
            </div>

            {/* Gestion des mois — statut + suppression différée (mois passés) */}
            {scheduleMonths.length > 0 && (
              <div className="schedule-months-manager" style={{ margin: '0 0 20px', border: '1px solid var(--border-color, #333)', borderRadius: 8, overflow: 'hidden' }}>
                <div style={{ padding: '8px 12px', fontWeight: 600, background: 'rgba(255,255,255,0.03)' }}>
                  Gestion des mois ({scheduleMonths.length}) — suppression réservée aux mois passés, purge définitive à J+{scheduleGraceDays}
                </div>
                <div style={{ maxHeight: 260, overflowY: 'auto' }}>
                  {scheduleMonths.map(m => {
                    const [yy, mm] = m.monthYear.split('-').map(Number);
                    const label = new Date(yy, mm - 1, 1).toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' });
                    const badge = m.status === 'current'
                      ? { txt: '● En cours', color: '#22c55e' }
                      : m.status === 'past'
                      ? { txt: 'Passé', color: '#9ca3af' }
                      : { txt: 'À venir', color: '#60a5fa' };
                    return (
                      <div key={m.monthYear} style={{
                        display: 'flex', alignItems: 'center', gap: 10, padding: '8px 12px',
                        borderTop: '1px solid var(--border-color, #2a2a2a)',
                        background: m.deletionScheduledAt ? 'rgba(239,68,68,0.08)' : 'transparent'
                      }}>
                        <span style={{ minWidth: 150, fontWeight: 600 }}>{label}</span>
                        <span style={{ fontSize: 12, color: badge.color, minWidth: 80 }}>{badge.txt}</span>
                        <span style={{ fontSize: 12, opacity: 0.7 }}>{m.slots} slots</span>
                        {m.deletionScheduledAt && (
                          <span style={{ fontSize: 12, color: '#ef4444', marginLeft: 4 }}>
                            🗑️ suppression dans {m.daysLeft} j
                          </span>
                        )}
                        <span style={{ marginLeft: 'auto', display: 'flex', gap: 6 }}>
                          {m.deletionScheduledAt ? (
                            <button className="btn-secondary" style={{ fontSize: 12, padding: '4px 10px' }}
                              onClick={() => handleRestoreMonth(m.monthYear)}>♻️ Restaurer</button>
                          ) : m.canDelete ? (
                            <button className="btn-danger" style={{ fontSize: 12, padding: '4px 10px' }}
                              onClick={() => handleDeleteMonth(m.monthYear)}>🗑️ Effacer</button>
                          ) : (
                            <span style={{ fontSize: 11, opacity: 0.4 }}>protégé</span>
                          )}
                        </span>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {scheduleLoading ? (
              <div className="admin-loading">{t('common.loading')}</div>
            ) : schedule ? (
              <>
                {/* Stats du mois */}
                <div className="schedule-stats">
                  <div className="schedule-stat-card">
                    <span className="schedule-stat-icon icon-calendar"><IconCalendar /></span>
                    <div className="schedule-stat-body">
                      <span className="schedule-stat-value">{schedule.monthYear}</span>
                      <span className="schedule-stat-label">{t('admin.month')}</span>
                    </div>
                  </div>
                  <div className="schedule-stat-card">
                    <span className="schedule-stat-icon icon-fees"><IconChart /></span>
                    <div className="schedule-stat-body">
                      <span className="schedule-stat-value">{schedule.totalSlots}</span>
                      <span className="schedule-stat-label">{t('admin.totalSlots')}</span>
                    </div>
                  </div>
                  <div className="schedule-stat-card protected">
                    <span className="schedule-stat-icon icon-blocked"><IconLock /></span>
                    <div className="schedule-stat-body">
                      <span className="schedule-stat-value">{schedule.protectedSlots || 0}</span>
                      <span className="schedule-stat-label">{t('admin.distributed')}</span>
                    </div>
                  </div>
                  <div className="schedule-stat-card">
                    <span className="schedule-stat-icon icon-trades"><IconTrendingUp /></span>
                    <div className="schedule-stat-body">
                      <span className="schedule-stat-value">
                        ×{schedule.monthlyMultiplier} (+{schedule.monthlyGrowthPercent}%)
                      </span>
                      <span className="schedule-stat-label">Croissance du mois (bronze)</span>
                    </div>
                  </div>
                </div>

                <div className="schedule-legend">
                  <span className="legend-item"><span className="legend-dot distributed"></span> Distribué (protégé)</span>
                  <span className="legend-item"><span className="legend-dot edited"></span> Modifié manuellement</span>
                  <span className="legend-item"><span className="legend-dot future"></span> Futur (modifiable)</span>
                </div>

                {/* Calendrier des jours */}
                <div className="schedule-calendar">
                  <h3>Sélectionnez un jour pour voir/éditer les trades</h3>
                  <div className="schedule-days-grid">
                    {Object.keys(schedule.schedule).map(day => {
                      const dayNum = parseInt(day);
                      const slots = schedule.schedule[day];
                      // Passé/aujourd'hui calculés sur le MOIS affiché (pas juste le jour)
                      const now = new Date();
                      const [selYear, selMonth] = scheduleMonth.split('-').map(Number);
                      const dayDate = new Date(selYear, selMonth - 1, dayNum);
                      const todayDate = new Date(now.getFullYear(), now.getMonth(), now.getDate());
                      const isToday = dayDate.getTime() === todayDate.getTime();
                      const isPast = dayDate < todayDate;
                      const hasDistributed = slots.some(s => s.distributed);
                      const hasEdited = slots.some(s => s.manuallyEdited);

                      return (
                        <div
                          key={day}
                          className={`schedule-day-card ${selectedDay === dayNum ? 'selected' : ''} ${isToday ? 'today' : ''} ${isPast ? 'past' : ''} ${hasDistributed ? 'has-distributed' : ''} ${hasEdited ? 'has-edited' : ''}`}
                          onClick={() => loadDaySlots(dayNum)}
                        >
                          <span className="day-number">{day}</span>
                          <span className="day-slots-count">{slots.length} trades</span>
                          {isToday && <span className="today-badge">{t('admin.today')}</span>}
                          {hasDistributed && <span className="distributed-indicator">🔒</span>}
                          {hasEdited && <span className="edited-indicator">✏️</span>}
                        </div>
                      );
                    })}
                  </div>
                </div>

                {/* Détail du jour sélectionné */}
                {selectedDay && (
                  <div className="schedule-day-detail">
                    <h3>{t('admin.tradesForDay')} {selectedDay}</h3>
                    <div className="schedule-slots-list">
                      {daySlots.map((slot, index) => {
                        // Vérifier si le slot est dans le passé (en UTC comme le backend)
                        const now = new Date();
                        const [slotHour, slotMinute] = slot.hour.split(':').map(Number);
                        const [selYear, selMonth] = scheduleMonth.split('-').map(Number);
                        // Créer la date du slot en UTC
                        const slotDateTime = new Date(Date.UTC(selYear, selMonth - 1, selectedDay, slotHour, slotMinute));

                        const isSlotPast = slotDateTime <= now;

                        const canEdit = !slot.distributed && !isSlotPast;

                        return (
                        <div key={index} className={`schedule-slot-card ${slot.type.toLowerCase()} ${slot.distributed ? 'distributed' : ''} ${slot.manuallyEdited ? 'edited' : ''} ${isSlotPast ? 'past' : ''}`}>
                          <div className="slot-header">
                            <span className="slot-time">{(() => {
                              const [h, m] = slot.hour.split(':').map(Number);
                              const paris = utcToParis(h, m);
                              return formatTime(paris.hour, paris.minute);
                            })()} (Paris)</span>
                            <div className="slot-badges">
                              {slot.distributed && <span className="slot-badge distributed">🔒 {t('admin.distributed')}</span>}
                              {slot.manuallyEdited && <span className="slot-badge edited">✏️ {t('admin.modified')}</span>}
                              <span className="slot-number">{t('admin.slot')} {slot.slot}</span>
                            </div>
                          </div>

                          {editingSlot === slot.id ? (
                            <div className="slot-edit-form">
                              <div className="edit-row">
                                <label>{t('admin.actualGain')} (%)</label>
                                <input
                                  type="number"
                                  step="0.01"
                                  value={slotEditForm.actualGainPercent}
                                  onChange={(e) => setSlotEditForm({...slotEditForm, actualGainPercent: parseFloat(e.target.value)})}
                                />
                              </div>
                              <div className="edit-row">
                                <label>{t('admin.potentialGain')} (%)</label>
                                <input
                                  type="number"
                                  step="0.01"
                                  value={slotEditForm.potentialGainPercent}
                                  onChange={(e) => setSlotEditForm({...slotEditForm, potentialGainPercent: parseFloat(e.target.value)})}
                                />
                              </div>
                              <div className="edit-row">
                                <label>Crypto</label>
                                <input
                                  type="text"
                                  value={slotEditForm.crypto}
                                  onChange={(e) => setSlotEditForm({...slotEditForm, crypto: e.target.value.toUpperCase()})}
                                />
                              </div>
                              <div className="edit-row">
                                <label>Type</label>
                                <select
                                  value={slotEditForm.type}
                                  onChange={(e) => setSlotEditForm({...slotEditForm, type: e.target.value})}
                                >
                                  <option value="BUY">{t('admin.buy')}</option>
                                  <option value="SELL">{t('admin.sell')}</option>
                                </select>
                              </div>
                              <div className="edit-row">
                                <label>{t('admin.slotTime') || 'Heure (Paris)'}</label>
                                <div style={{display: 'flex', gap: '5px', alignItems: 'center'}}>
                                  <input
                                    type="number"
                                    min="0"
                                    max="23"
                                    style={{width: '60px'}}
                                    value={slotEditForm.slotHour ?? 0}
                                    onChange={(e) => setSlotEditForm({...slotEditForm, slotHour: parseInt(e.target.value)})}
                                  />
                                  <span>:</span>
                                  <input
                                    type="number"
                                    min="0"
                                    max="59"
                                    style={{width: '60px'}}
                                    value={slotEditForm.slotMinute ?? 0}
                                    onChange={(e) => setSlotEditForm({...slotEditForm, slotMinute: parseInt(e.target.value)})}
                                  />
                                </div>
                              </div>
                              <div className="edit-row">
                                <label>Delai de demarrage (min)</label>
                                <input
                                  type="number"
                                  min="1"
                                  max="120"
                                  value={slotEditForm.startDelayMinutes ?? 20}
                                  onChange={(e) => setSlotEditForm({...slotEditForm, startDelayMinutes: parseInt(e.target.value)})}
                                  style={{width: '80px'}}
                                />
                              </div>
                              <div className="edit-actions">
                                <button className="btn-save-slot" onClick={handleSaveSlot}>{t('admin.save')}</button>
                                <button className="btn-cancel-slot" onClick={() => setEditingSlot(null)}>{t('admin.cancel')}</button>
                              </div>
                            </div>
                          ) : (
                            <div className="slot-body">
                              <div className="slot-crypto">
                                <span className="crypto-name">{slot.crypto}</span>
                                <span className={`trade-type ${slot.type.toLowerCase()}`}>
                                  {slot.type === 'BUY' ? `📈 ${t('admin.buy')}` : `📉 ${t('admin.sell')}`}
                                </span>
                              </div>
                              <div className="slot-details">
                                <div className="slot-detail">
                                  <span className="detail-label">{t('admin.potentialGain')}</span>
                                  <span className="detail-value positive">+{slot.potentialGain.toFixed(2)}%</span>
                                </div>
                                <div className="slot-detail">
                                  <span className="detail-label">{t('admin.actualGain')}</span>
                                  <span className={`detail-value ${slot.actualGain >= 0 ? 'positive' : 'negative'}`}>
                                    {slot.actualGain >= 0 ? '+' : ''}{slot.actualGain.toFixed(2)}%
                                  </span>
                                </div>
                                <div className="slot-detail">
                                  <span className="detail-label">{t('admin.duration')}</span>
                                  <span className="detail-value">{slot.duration} min</span>
                                </div>
                                <div className="slot-detail">
                                  <span className="detail-label">{t('admin.confidence')}</span>
                                  <span className="detail-value">{slot.confidence}%</span>
                                </div>
                                <div className="slot-detail">
                                  <span className="detail-label">Delai demarrage</span>
                                  <span className="detail-value">{slot.startDelayMinutes || 20} min</span>
                                </div>
                              </div>

                              {/* Stats du slot */}
                              {slot.stats && (
                                <div className="slot-stats">
                                  <div className="slot-stats-row">
                                    <span className="stat-item eligible">
                                      👥 {slot.stats.distributed}/{slot.stats.eligible}
                                    </span>
                                    <span className="stat-item accepted" title={t('admin.usersValidated')}>
                                      ✅ {slot.stats.accepted}
                                    </span>
                                    <span className="stat-item rejected" title={t('admin.usersRejected')}>
                                      ❌ {slot.stats.rejected}
                                    </span>
                                    <span className="stat-item expired" title={t('admin.usersMissed')}>
                                      ⏰ {slot.stats.expired}
                                    </span>
                                  </div>
                                </div>
                              )}
                              <button
                                className="btn-view-users"
                                onClick={() => loadSlotUsers(slot.id)}
                              >
                                {slot.distributed ? '👁️ Voir details' : '👥 Voir eligibles'}
                              </button>
                              {canEdit ? (
                                <button className="btn-edit-slot" onClick={() => handleEditSlot(slot)}>
                                  ✏️ {t('admin.modify')}
                                </button>
                              ) : isSlotPast && !slot.distributed ? (
                                <span className="slot-past-label">⏰ {t('admin.pastTime')}</span>
                              ) : null}
                            </div>
                          )}
                        </div>
                      );
                      })}
                    </div>
                  </div>
                )}

                {/* Boutons d'action */}
                <div className="schedule-actions">
                  <button
                    className="schedule-regenerate-btn"
                    onClick={handleRegenerateFuture}
                    disabled={regenerating}
                  >
                    {regenerating ? `⏳ ${t('admin.regenerating')}` : `🔄 ${t('admin.regenerateFutureSlots')}`}
                  </button>
                  <p className="schedule-warning">
                    {t('admin.protectedSlotsWarning')}
                  </p>
                </div>

                {/* Modale: Liste des users d'un slot */}
                {viewingSlotUsers && (
                  <div className="slot-users-modal-overlay" onClick={closeSlotUsers}>
                    <div className="slot-users-modal" onClick={(e) => e.stopPropagation()}>
                      <div className="slot-users-header">
                        <h3>
                          Slot {viewingSlotUsers.slotIndex + 1} - {viewingSlotUsers.crypto} {viewingSlotUsers.type}
                          <span className="slot-users-time">({viewingSlotUsers.hour})</span>
                          {viewingSlotUsers.startDelayMinutes && (
                            <span className="slot-users-delay"> - Delai: {viewingSlotUsers.startDelayMinutes}min</span>
                          )}
                        </h3>
                        <button className="close-modal" onClick={closeSlotUsers}>×</button>
                      </div>

                      {/* Filtres */}
                      <div className="slot-users-filters">
                        {viewingSlotUsers.distributed ? (
                          <>
                            <label>
                              <input
                                type="checkbox"
                                checked={slotUsersFilters.accepted}
                                onChange={(e) => setSlotUsersFilters({...slotUsersFilters, accepted: e.target.checked})}
                              />
                              ✅ Acceptes ({slotUsers.filter(u => ['accepted', 'completed'].includes(u.status)).length})
                            </label>
                            <label>
                              <input
                                type="checkbox"
                                checked={slotUsersFilters.rejected}
                                onChange={(e) => setSlotUsersFilters({...slotUsersFilters, rejected: e.target.checked})}
                              />
                              ❌ Rejetes ({slotUsers.filter(u => u.status === 'rejected').length})
                            </label>
                            <label>
                              <input
                                type="checkbox"
                                checked={slotUsersFilters.expired}
                                onChange={(e) => setSlotUsersFilters({...slotUsersFilters, expired: e.target.checked})}
                              />
                              Expires ({slotUsers.filter(u => u.status === 'expired').length})
                            </label>
                            <label>
                              <input
                                type="checkbox"
                                checked={slotUsersFilters.pending}
                                onChange={(e) => setSlotUsersFilters({...slotUsersFilters, pending: e.target.checked})}
                              />
                              En attente ({slotUsers.filter(u => u.status === 'pending').length})
                            </label>
                          </>
                        ) : (
                          <>
                            <label>
                              <input
                                type="checkbox"
                                checked={slotUsersFilters.eligible}
                                onChange={(e) => setSlotUsersFilters({...slotUsersFilters, eligible: e.target.checked})}
                              />
                              ✅ Eligibles ({slotUsers.length})
                            </label>
                            <label>
                              <input
                                type="checkbox"
                                checked={slotUsersFilters.notEligible}
                                onChange={(e) => setSlotUsersFilters({...slotUsersFilters, notEligible: e.target.checked})}
                              />
                              ❌ Non eligibles ({notEligibleUsers.length})
                            </label>
                          </>
                        )}
                      </div>

                      {/* Liste des users */}
                      <div className="slot-users-list">
                        {slotUsersLoading ? (
                          <p className="loading">Chargement...</p>
                        ) : filteredSlotUsers.length === 0 ? (
                          <p className="no-users">Aucun utilisateur avec ces filtres</p>
                        ) : viewingSlotUsers.distributed ? (
                          filteredSlotUsers.map((u, idx) => (
                            <div key={idx} className={`slot-user-item ${u.status}`}>
                              <div className="user-info">
                                <span className="user-name">{u.username}</span>
                                <span className="user-rank">{u.rank}</span>
                              </div>
                              <div className="user-amount">{u.amount?.toFixed(2) || '0.00'} USDT</div>
                              <div className={`user-status ${u.status}`}>
                                {u.status === 'accepted' && '✅ Accepte'}
                                {u.status === 'completed' && '✅ Termine'}
                                {u.status === 'rejected' && '❌ Rejete'}
                                {u.status === 'expired' && 'Expire'}
                                {u.status === 'pending' && 'En attente'}
                              </div>
                              {u.gainPercent !== null && (
                                <div className={`user-gain ${u.gainPercent >= 0 ? 'positive' : 'negative'}`}>
                                  {u.gainPercent >= 0 ? '+' : ''}{u.gainPercent.toFixed(2)}%
                                </div>
                              )}
                            </div>
                          ))
                        ) : (
                          filteredSlotUsers.map((u, idx) => (
                            <div key={idx} className={`slot-user-item ${u.willReceive ? 'eligible' : 'not_eligible'}`}>
                              <div className="user-info">
                                <span className="user-name">{u.username}</span>
                                <span className="user-rank">{u.rank}</span>
                              </div>
                              <div className="user-balance">{u.balance?.toFixed(2) || '0.00'} USDT</div>
                              <div className="user-slots">
                                Max: {u.maxSlots} slots/jour
                              </div>
                              <div className={`user-status ${u.willReceive ? 'eligible' : 'not_eligible'}`}>
                                {u.willReceive ? '✅ Recevra ce slot' : '❌ Rang insuffisant'}
                              </div>
                            </div>
                          ))
                        )}
                      </div>
                    </div>
                  </div>
                )}
              </>
            ) : (
              <p className="no-data">{t('admin.noSchedule')}</p>
            )}
          </div>
        )}

        {/* System Tab */}
        {/* KYC Tab */}
        {activeTab === 'kyc' && (
          <div className="admin-kyc">
            <h2>Vérification d'identité (KYC)</h2>

            {/* Stats */}
            {kycStats && (
              <div className="kyc-stats">
                <div className="kyc-stat-card pending">
                  <span className="kyc-stat-icon">⏳</span>
                  <div className="kyc-stat-body">
                    <span className="kyc-stat-value">{kycStats.pending}</span>
                    <span className="kyc-stat-label">En attente</span>
                  </div>
                </div>
                <div className="kyc-stat-card verified">
                  <span className="kyc-stat-icon">✅</span>
                  <div className="kyc-stat-body">
                    <span className="kyc-stat-value">{kycStats.verified}</span>
                    <span className="kyc-stat-label">Vérifiés</span>
                  </div>
                </div>
                <div className="kyc-stat-card rejected">
                  <span className="kyc-stat-icon">❌</span>
                  <div className="kyc-stat-body">
                    <span className="kyc-stat-value">{kycStats.rejected}</span>
                    <span className="kyc-stat-label">Rejetés</span>
                  </div>
                </div>
                <div className="kyc-stat-card unverified">
                  <span className="kyc-stat-icon">👤</span>
                  <div className="kyc-stat-body">
                    <span className="kyc-stat-value">{kycStats.unverified}</span>
                    <span className="kyc-stat-label">Non soumis</span>
                  </div>
                </div>
              </div>
            )}

            {/* Demandes en attente */}
            <div className="kyc-section">
              <div className="kyc-section-header">
                <h3>Demandes en attente</h3>
                <button className="kyc-refresh-btn" onClick={loadKyc}>
                  ↻ Rafraîchir
                </button>
              </div>

              {kycLoading ? (
                <div className="admin-loading">Chargement...</div>
              ) : kycRequests.length === 0 ? (
                <div className="kyc-empty">
                  <span>✓</span>
                  <p>Aucune demande en attente</p>
                </div>
              ) : (
                <div className="kyc-list">
                  {kycRequests.map(req => (
                    <div key={req.id} className="kyc-card">
                      <div className="kyc-card-header">
                        <div className="kyc-user-info">
                          <span className="kyc-username">{req.username}</span>
                          <span className="kyc-email">{req.email}</span>
                        </div>
                        <div className="kyc-doc-info">
                          <span className="kyc-doc-type">{req.kycDocumentType}</span>
                          <span className="kyc-doc-number">{req.kycDocumentNumber}</span>
                        </div>
                        <span className="kyc-date">
                          {new Date(req.kycSubmittedAt).toLocaleDateString('fr-FR', {
                            day: '2-digit', month: '2-digit', year: 'numeric',
                            hour: '2-digit', minute: '2-digit'
                          })}
                        </span>
                      </div>

                      {kycRejectingId === req.id ? (
                        <div className="kyc-reject-form">
                          <input
                            type="text"
                            placeholder="Raison du rejet (obligatoire)"
                            value={kycRejectReason}
                            onChange={e => setKycRejectReason(e.target.value)}
                            className="kyc-reject-input"
                          />
                          <div className="kyc-reject-actions">
                            <button
                              className="kyc-btn-cancel"
                              onClick={() => { setKycRejectingId(null); setKycRejectReason(''); }}
                            >
                              Annuler
                            </button>
                            <button
                              className="kyc-btn-reject"
                              onClick={() => handleRejectKyc(req.id)}
                              disabled={kycActionLoading === req.id + '_reject'}
                            >
                              {kycActionLoading === req.id + '_reject' ? '...' : 'Confirmer le rejet'}
                            </button>
                          </div>
                        </div>
                      ) : (
                        <div className="kyc-card-actions">
                          <button
                            className="kyc-btn-verify"
                            onClick={() => handleVerifyKyc(req.id)}
                            disabled={kycActionLoading === req.id + '_verify'}
                          >
                            {kycActionLoading === req.id + '_verify' ? '...' : '✓ Valider'}
                          </button>
                          <button
                            className="kyc-btn-reject-open"
                            onClick={() => { setKycRejectingId(req.id); setKycRejectReason(''); }}
                          >
                            ✗ Rejeter
                          </button>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Deposits Tab */}
        {activeTab === 'deposits' && (
          <div className="admin-deposits">
            {/* Adresse TRC20 affichée aux utilisateurs */}
            <div className="deposit-decoder-card">
              <label className="deposit-decoder-label">Adresse TRC20 affichée aux utilisateurs (dépôt)</label>
              <div className="deposit-address-current">
                {depositAddressSetting
                  ? <span className="binance-address-value">{depositAddressSetting}</span>
                  : <span className="no-data">Aucune adresse configurée</span>}
              </div>
              <input
                type="text"
                className="deposit-token-input"
                value={depositAddressInput}
                onChange={(e) => setDepositAddressInput(e.target.value.trim())}
                placeholder="Nouvelle adresse TRC20 (commence par T, 34 caractères)"
              />
              <button
                className="deposit-decode-btn"
                onClick={handleSaveDepositAddress}
                disabled={depositAddressSaving || depositAddressInput.trim().length < 34}
              >
                {depositAddressSaving ? 'Enregistrement...' : 'Enregistrer l\'adresse'}
              </button>
            </div>

            {/* Statistiques de dépôts par période */}
            <div className="wd-section deposit-stats-section">
              <h3>📊 Statistiques des dépôts validés</h3>

              {/* Sélecteur de période */}
              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center', margin: '0 0 14px' }}>
                {[
                  { k: 'day', label: "Aujourd'hui" },
                  { k: 'week', label: 'Semaine' },
                  { k: 'month', label: 'Ce mois' },
                  { k: 'all', label: 'Depuis toujours' }
                ].map(p => (
                  <button
                    key={p.k}
                    onClick={() => handleDepositPeriod(p.k)}
                    style={{
                      padding: '6px 12px', borderRadius: 6, cursor: 'pointer', fontSize: 13,
                      border: '1px solid var(--border-color, #333)',
                      background: depositStatsPeriod === p.k ? 'var(--accent, #f0b90b)' : 'transparent',
                      color: depositStatsPeriod === p.k ? '#000' : 'inherit',
                      fontWeight: depositStatsPeriod === p.k ? 700 : 400
                    }}
                  >{p.label}</button>
                ))}
                <span style={{ opacity: 0.5, margin: '0 2px' }}>|</span>
                <input
                  type="month"
                  value={depositStatsMonth}
                  onChange={(e) => { const m = e.target.value; setDepositStatsMonth(m); setDepositStatsPeriod('custom'); if (m) loadDepositStats('custom', m); }}
                  style={{ padding: '5px 8px', borderRadius: 6, border: '1px solid var(--border-color, #333)', background: 'transparent', color: 'inherit' }}
                  title="Choisir un mois précis"
                />
              </div>

              {depositStatsLoading ? (
                <p className="no-data">Chargement…</p>
              ) : depositStats ? (
                <>
                  <div style={{ fontSize: 12, opacity: 0.6, marginBottom: 10 }}>{depositStats.period?.label}</div>
                  {/* Cartes résumé */}
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 10, marginBottom: 16 }}>
                    {[
                      { lbl: 'Total validé', val: formatCurrency(depositStats.approved.sum), sub: `${depositStats.approved.count} dépôt(s)`, hi: true },
                      { lbl: 'Dépôt moyen', val: formatCurrency(depositStats.approved.avg) },
                      { lbl: 'Plus gros dépôt', val: formatCurrency(depositStats.approved.max) },
                      { lbl: 'En attente', val: formatCurrency(depositStats.pending.sum), sub: `${depositStats.pending.count} en attente` }
                    ].map((c, i) => (
                      <div key={i} style={{
                        border: '1px solid var(--border-color, #2a2a2a)', borderRadius: 8, padding: '12px 14px',
                        background: c.hi ? 'rgba(240,185,11,0.08)' : 'rgba(255,255,255,0.02)'
                      }}>
                        <div style={{ fontSize: 12, opacity: 0.7 }}>{c.lbl}</div>
                        <div style={{ fontSize: 20, fontWeight: 700, margin: '2px 0' }}>{c.val}</div>
                        {c.sub && <div style={{ fontSize: 11, opacity: 0.6 }}>{c.sub}</div>}
                      </div>
                    ))}
                  </div>

                  {/* Top dépôts triés par montant */}
                  {depositStats.top?.length > 0 && (
                    <div>
                      <div style={{ fontWeight: 600, fontSize: 13, marginBottom: 6 }}>Top dépôts (par montant)</div>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                        {depositStats.top.map((d, i) => (
                          <div key={i} style={{ display: 'flex', justifyContent: 'space-between', gap: 10, fontSize: 13, padding: '4px 8px', borderBottom: '1px solid var(--border-color, #222)' }}>
                            <span style={{ opacity: 0.5, width: 20 }}>{i + 1}.</span>
                            <span style={{ flex: 1, fontWeight: 600 }}>{d.username}</span>
                            <span style={{ opacity: 0.6, fontSize: 12 }}>{d.date ? new Date(d.date).toLocaleDateString('fr-FR') : '—'}</span>
                            <span style={{ fontWeight: 700, minWidth: 90, textAlign: 'right' }}>{formatCurrency(d.amount)}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  {depositStats.approved.count === 0 && (
                    <p className="no-data">Aucun dépôt validé sur cette période.</p>
                  )}
                </>
              ) : (
                <p className="no-data">Aucune donnée.</p>
              )}
            </div>

            {/* Dépôts par virement réel (TXID) en attente */}
            <div className="wd-section">
              <h3>
                Dépôts en attente (virement TRC20)
                {pendingDeposits.length > 0 && (
                  <span className="binance-pending-count">{pendingDeposits.length}</span>
                )}
              </h3>
              {depositsTxidLoading ? (
                <p className="no-data">Chargement...</p>
              ) : pendingDeposits.length === 0 ? (
                <p className="no-data">Aucun dépôt en attente</p>
              ) : (
                <div className="binance-admin-list">
                  {pendingDeposits.map((d) => (
                    <div key={d.id} className="binance-admin-item">
                      <div className="binance-admin-user">
                        <span className="wd-username">{d.username}</span>
                        <span className="wd-email">{d.email}</span>
                        <span className="wd-email">Solde actuel: {d.userBalance.toFixed(2)} USDT</span>
                      </div>
                      <div className="binance-admin-address">
                        <span className="binance-network-chip">{d.network}</span>
                        <span className="binance-address-value">{d.txid}</span>
                      </div>
                      <div className="binance-admin-address">
                        <span className="dpr-label">Montant déclaré</span>
                        <span className="dpr-value dpr-balance">{d.amount.toFixed(2)} USDT</span>
                      </div>
                      {depositTxidRejectingId === d.id ? (
                        <div className="binance-admin-reject">
                          <input
                            type="text"
                            placeholder="Raison du rejet"
                            value={depositTxidRejectReason}
                            onChange={(e) => setDepositTxidRejectReason(e.target.value)}
                          />
                          <button
                            className="btn-reject"
                            disabled={depositTxidActionLoading === d.id}
                            onClick={() => handleDepositTxid(d.id, 'reject', depositTxidRejectReason)}
                          >
                            Confirmer le rejet
                          </button>
                          <button className="btn-cancel-slot" onClick={() => setDepositTxidRejectingId(null)}>
                            Annuler
                          </button>
                        </div>
                      ) : (
                        <div className="binance-admin-actions">
                          <button
                            className="btn-approve"
                            disabled={depositTxidActionLoading === d.id}
                            onClick={() => handleDepositTxid(d.id, 'approve')}
                          >
                            ✓ Valider et créditer {d.amount.toFixed(2)} USDT
                          </button>
                          <button
                            className="btn-reject"
                            disabled={depositTxidActionLoading === d.id}
                            onClick={() => setDepositTxidRejectingId(d.id)}
                          >
                            ✕ Rejeter
                          </button>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>

            <h2>Décodeur de Tokens de Dépôt</h2>
            <p className="deposits-desc">
              Collez ici le token fourni par un utilisateur pour identifier son compte et créditer son solde.
            </p>

            <div className="deposit-decoder-card">
              <label className="deposit-decoder-label">Token de dépôt</label>
              <textarea
                className="deposit-token-input"
                value={depositTokenInput}
                onChange={(e) => { setDepositTokenInput(e.target.value); setDecodeError(''); setDecodedDepositResult(null); setDepositCreditSuccess(''); }}
                placeholder="Collez le code de recharge de l'utilisateur ici..."
                rows={4}
              />
              {decodeError && <div className="deposit-decode-error">{decodeError}</div>}
              <button
                className="deposit-decode-btn"
                onClick={handleDecodeDepositToken}
                disabled={decodeLoading || !depositTokenInput.trim()}
              >
                {decodeLoading ? 'Décodage...' : '🔑 Décoder le token'}
              </button>
            </div>

            {decodedDepositResult && (
              <div className="deposit-result-card">
                <div className="deposit-result-header">
                  <span className="deposit-result-icon">✅</span>
                  <h3>Token valide — Utilisateur identifié</h3>
                </div>

                <div className="deposit-result-user">
                  <div className="deposit-result-row">
                    <span className="dpr-label">Utilisateur</span>
                    <span className="dpr-value">{decodedDepositResult.user.username}</span>
                  </div>
                  <div className="deposit-result-row">
                    <span className="dpr-label">Email</span>
                    <span className="dpr-value">{decodedDepositResult.user.email}</span>
                  </div>
                  <div className="deposit-result-row">
                    <span className="dpr-label">Rang</span>
                    <span className="dpr-value dpr-rank">{decodedDepositResult.user.rank}</span>
                  </div>
                  <div className="deposit-result-row">
                    <span className="dpr-label">Solde actuel</span>
                    <span className="dpr-value dpr-balance">{(parseFloat(decodedDepositResult.user.balance) || 0).toFixed(2)} USDT</span>
                  </div>
                  <div className="deposit-result-row">
                    <span className="dpr-label">Token émis le</span>
                    <span className="dpr-value">{new Date(decodedDepositResult.decoded.issuedAt).toLocaleString('fr-FR')}</span>
                  </div>
                  <div className="deposit-result-row">
                    <span className="dpr-label">Expire le</span>
                    <span className="dpr-value">{new Date(decodedDepositResult.decoded.expiresAt).toLocaleString('fr-FR')}</span>
                  </div>
                </div>

                <div className="deposit-credit-form">
                  <h4>Créditer le compte</h4>
                  {depositCreditSuccess && (
                    <div className="deposit-credit-success">{depositCreditSuccess}</div>
                  )}
                  <div className="deposit-credit-inputs">
                    <div className="dci-group">
                      <label>Montant ($)</label>
                      <input
                        type="number"
                        value={depositCreditAmount}
                        onChange={(e) => setDepositCreditAmount(e.target.value)}
                        placeholder="0.00"
                        min="0"
                        className="dci-input"
                      />
                    </div>
                    <div className="dci-group">
                      <label>Motif (optionnel)</label>
                      <input
                        type="text"
                        value={depositCreditReason}
                        onChange={(e) => setDepositCreditReason(e.target.value)}
                        placeholder="Dépôt via token"
                        className="dci-input"
                      />
                    </div>
                  </div>
                  <button
                    className="deposit-credit-btn"
                    onClick={handleCreditFromDeposit}
                    disabled={depositCreditLoading || !depositCreditAmount || parseFloat(depositCreditAmount) <= 0}
                  >
                    {depositCreditLoading ? 'Traitement...' : `💰 Créditer ${depositCreditAmount ? parseFloat(depositCreditAmount).toFixed(2) + ' USDT' : ''} → ${decodedDepositResult.user.username}`}
                  </button>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Messages Tab */}
        {activeTab === 'messages' && (
          <div className="admin-messages">
            <h2>💬 Messages Support</h2>

            {messagesLoading ? (
              <div className="admin-loading">Chargement...</div>
            ) : (
              <div className={`messages-admin-layout ${selectedConversation ? 'has-selection' : ''}`}>
                {/* Liste des conversations */}
                <div className="conversations-list">
                  <div className="conversations-list-header">
                    <h3>Conversations ({messageConversations.length})</h3>
                    <button
                      className="new-msg-btn"
                      title="Écrire à un utilisateur"
                      onClick={() => {
                        setShowNewMsgSearch(s => !s);
                        setNewMsgQuery('');
                        setNewMsgResults([]);
                      }}
                    >
                      {showNewMsgSearch ? '✕' : '✉️ Nouveau'}
                    </button>
                  </div>
                  {showNewMsgSearch && (
                    <div className="new-msg-search">
                      <input
                        type="text"
                        placeholder="Pseudo ou email…"
                        value={newMsgQuery}
                        onChange={(e) => handleNewMsgSearch(e.target.value)}
                        autoFocus
                      />
                      {newMsgResults.length > 0 && (
                        <div className="new-msg-results">
                          {newMsgResults.map(u => (
                            <div key={u.id} className="new-msg-result" onClick={() => handleNewMsgSelect(u.id)}>
                              <strong>{u.username}</strong>
                              <span>{u.email}</span>
                            </div>
                          ))}
                        </div>
                      )}
                      {newMsgQuery.trim().length >= 2 && newMsgResults.length === 0 && (
                        <p className="new-msg-empty">Aucun utilisateur trouvé</p>
                      )}
                    </div>
                  )}
                  {messageConversations.length === 0 ? (
                    <p className="no-data">Aucune conversation</p>
                  ) : (
                    messageConversations.map(conv => (
                      <div
                        key={conv.userId}
                        className={`conversation-item ${selectedConversation?.id === conv.userId ? 'active' : ''} ${conv.unreadCount > 0 ? 'has-unread' : ''}`}
                        onClick={() => loadConversation(conv.userId)}
                      >
                        <div className="conv-user">
                          <strong>{conv.user?.username || 'Utilisateur'}</strong>
                          {conv.unreadCount > 0 && (
                            <span className="unread-badge">{conv.unreadCount}</span>
                          )}
                          <span className="conv-email">{conv.user?.email}</span>
                        </div>
                        <div className="conv-meta">
                          <span className="conv-count">
                            {conv.pendingCount > 0
                              ? `${conv.pendingCount} en attente`
                              : conv.lastFromAdmin
                                ? (conv.lastSeen
                                    ? <span style={{ color: '#10b981', fontWeight: 600 }}>✓✓ Vu</span>
                                    : <span style={{ opacity: 0.6 }}>Envoyé · non lu</span>)
                                : 'Répondu'}
                          </span>
                          <span className="conv-date">
                            {new Date(conv.lastMessageAt).toLocaleDateString('fr-FR')}
                          </span>
                        </div>
                      </div>
                    ))
                  )}
                </div>

                {/* Détail conversation */}
                <div className="conversation-detail">
                  {selectedConversation ? (
                    <>
                      <div className="conv-header">
                        <button
                          className="conv-back-btn"
                          title="Retour aux conversations"
                          onClick={() => setSelectedConversation(null)}
                        >
                          ←
                        </button>
                        <h3>{selectedConversation.username}</h3>
                        <div className="conv-header-actions">
                          <label className="read-receipts-toggle">
                            <input
                              type="checkbox"
                              checked={selectedConversation.showReadReceipts || false}
                              onChange={async (e) => {
                                const newValue = e.target.checked;
                                try {
                                  await api.put(`/admin/messages/${selectedConversation.id}/read-receipts`, {
                                    enabled: newValue
                                  });
                                  // Mettre à jour selectedConversation
                                  setSelectedConversation(prev => ({
                                    ...prev,
                                    showReadReceipts: newValue
                                  }));
                                  // Mettre à jour aussi la liste des conversations
                                  setMessageConversations(prev =>
                                    prev.map(c => c.userId === selectedConversation.id
                                      ? { ...c, showReadReceipts: newValue }
                                      : c
                                    )
                                  );
                                  // Recharger les messages pour afficher/masquer readAt
                                  const res = await api.get(`/admin/messages/${selectedConversation.id}`);
                                  setConversationMessages(res.data.messages || []);
                                } catch (err) {
                                  alert('Erreur');
                                }
                              }}
                            />
                            <span>Confirmations de lecture</span>
                          </label>
                          <button
                            className="btn-danger btn-sm"
                            onClick={() => handleDeleteConversation(selectedConversation.id)}
                          >
                            🗑️ Supprimer
                          </button>
                        </div>
                      </div>

                      <div className="conv-messages" ref={convMessagesRef}>
                        {conversationMessages.length === 0 && (
                          <p className="no-data">Aucun message — écrivez le premier message à cet utilisateur.</p>
                        )}
                        {conversationMessages.map(msg => (
                          <div
                            key={msg.id}
                            className={`conv-msg ${msg.senderRole === 'user' ? 'from-user' : 'from-admin'}`}
                          >
                            <div className="msg-content">{renderRichText(msg.content)}</div>
                            <div className="msg-meta">
                              <span>{msg.senderRole === 'user' ? '👤 Utilisateur' : '🛡️ Admin'}</span>
                              <span>{new Date(msg.createdAt).toLocaleString('fr-FR')}</span>
                              {msg.senderRole === 'admin' && msg.readAt && (
                                <span className="msg-read-status">✓ Vu {new Date(msg.readAt).toLocaleString('fr-FR')}</span>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>

                      <div className="conv-reply">
                        <textarea
                          value={replyContent}
                          onChange={(e) => setReplyContent(e.target.value)}
                          placeholder={conversationMessages.length === 0 ? 'Votre message...' : 'Votre réponse...'}
                          rows={3}
                        />
                        <button
                          onClick={handleReply}
                          disabled={!replyContent.trim() || replySending}
                        >
                          {replySending ? 'Envoi...' : (conversationMessages.length === 0 ? 'Envoyer' : 'Répondre')}
                        </button>
                      </div>
                    </>
                  ) : (
                    <div className="no-selection">
                      <p>Sélectionnez une conversation</p>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        )}

        {activeTab === 'notifications' && (
          <div className="admin-notifications">
            <h2>🔔 Notifications par email</h2>
            <p className="admin-notif-intro">
              Reçois un email quand ces événements surviennent. Active ou désactive chaque
              type indépendamment. Les alertes en temps réel dans le panneau restent actives quoi qu'il arrive.
            </p>

            {notifLoading ? (
              <div className="admin-loading">Chargement...</div>
            ) : (
              <>
                <div className="notif-email-box">
                  <label>Adresse de réception</label>
                  <div className="notif-email-row">
                    <input
                      type="email"
                      value={notifEmail}
                      onChange={(e) => setNotifEmail(e.target.value)}
                      placeholder="support@cryptobotobk.com"
                    />
                    <button onClick={saveNotifEmail} disabled={notifEmailSaving}>
                      {notifEmailSaving ? '...' : 'Enregistrer'}
                    </button>
                  </div>
                  {notifSavedMsg && <span className="notif-saved">{notifSavedMsg}</span>}
                  <span className="notif-email-hint">
                    Les emails partent de {`admin@cryptobotobk.com`} vers cette adresse.
                  </span>
                </div>

                <div className="notif-list">
                  {NOTIF_EVENTS.map(ev => (
                    <div className="notif-item" key={ev.key}>
                      <span className="notif-item-icon">{ev.icon}</span>
                      <div className="notif-item-text">
                        <span className="notif-item-label">{ev.label}</span>
                        <span className="notif-item-desc">{ev.desc}</span>
                      </div>
                      <button
                        type="button"
                        role="switch"
                        aria-checked={!!notifSettings[ev.key]}
                        className={`notif-switch ${notifSettings[ev.key] ? 'on' : ''}`}
                        onClick={() => toggleNotif(ev.key)}
                      >
                        <span className="notif-switch-knob"></span>
                      </button>
                    </div>
                  ))}
                </div>

                {/* Notifications UTILISATEURS (signal manqué) — in-app / email, pas vers l'admin */}
                <h3 style={{ marginTop: 28 }}>Notifications utilisateurs (signal manqué)</h3>
                <p className="notif-email-hint" style={{ marginBottom: 12 }}>
                  Ces notifications s'adressent aux utilisateurs, pas à l'admin. Le message parrain est envoyé dans l'application (aucun email).
                </p>
                <div className="notif-list">
                  {USER_NOTIF_EVENTS.map(ev => (
                    <div className="notif-item" key={ev.key}>
                      <span className="notif-item-icon">{ev.icon}</span>
                      <div className="notif-item-text">
                        <span className="notif-item-label">{ev.label}</span>
                        <span className="notif-item-desc">{ev.desc}</span>
                      </div>
                      <button
                        type="button"
                        role="switch"
                        aria-checked={!!userNotifs[ev.key]}
                        className={`notif-switch ${userNotifs[ev.key] ? 'on' : ''}`}
                        onClick={() => toggleUserNotif(ev.key)}
                      >
                        <span className="notif-switch-knob"></span>
                      </button>
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>
        )}

        {activeTab === 'broadcast' && (
          <div className="admin-broadcast">
            <h2>📣 Diffusion groupée</h2>
            <p className="notif-email-hint" style={{ marginBottom: 16 }}>
              Envoyer un message à un ensemble d'utilisateurs. Les comptes admin/manager et bloqués sont toujours exclus.
            </p>

            {(() => {
              const seg = (val, cur, label, onClick) => (
                <button type="button" onClick={onClick} style={{
                  padding: '6px 12px', borderRadius: 6, cursor: 'pointer', fontSize: 13,
                  border: '1px solid var(--border-color, #333)',
                  background: cur === val ? 'var(--accent, #f0b90b)' : 'transparent',
                  color: cur === val ? '#000' : 'inherit', fontWeight: cur === val ? 700 : 400
                }}>{label}</button>
              );
              return (
                <>
                  {/* Ciblage */}
                  <div style={{ marginBottom: 12 }}>
                    <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 6 }}>Vérification email</div>
                    <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                      {seg('all', bcAudience.verified, 'Tous', () => setBcAud({ verified: 'all' }))}
                      {seg('verified', bcAudience.verified, 'Email vérifié', () => setBcAud({ verified: 'verified' }))}
                      {seg('unverified', bcAudience.verified, 'Non vérifiés', () => setBcAud({ verified: 'unverified' }))}
                    </div>
                  </div>
                  <div style={{ marginBottom: 12 }}>
                    <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 6 }}>Capital</div>
                    <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                      {seg('all', bcAudience.capital, 'Tous', () => setBcAud({ capital: 'all' }))}
                      {seg('invested', bcAudience.capital, 'Ont investi', () => setBcAud({ capital: 'invested' }))}
                      {seg('notinvested', bcAudience.capital, 'N\'ont pas investi', () => setBcAud({ capital: 'notinvested' }))}
                    </div>
                  </div>
                </>
              );
            })()}

            <div style={{ margin: '10px 0 18px', fontSize: 15 }}>
              🎯 <strong>{bcCount ?? '…'}</strong> utilisateur(s) seront ciblés.
            </div>

            {/* Canaux */}
            <div style={{ marginBottom: 14 }}>
              <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 6 }}>Canaux</div>
              <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6, marginRight: 18, cursor: 'pointer' }}>
                <input type="checkbox" checked={bcChannels.inapp} onChange={(e) => setBcChannels({ ...bcChannels, inapp: e.target.checked })} />
                💬 Messagerie in-app
              </label>
              <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6, cursor: 'pointer' }}>
                <input type="checkbox" checked={bcChannels.email} onChange={(e) => setBcChannels({ ...bcChannels, email: e.target.checked })} />
                📧 Email
              </label>
            </div>

            {/* Contenu */}
            {bcChannels.email && (
              <input
                type="text"
                value={bcSubject}
                onChange={(e) => setBcSubject(e.target.value)}
                placeholder="Objet de l'email"
                style={{ width: '100%', maxWidth: 560, padding: '8px 10px', borderRadius: 6, marginBottom: 10, border: '1px solid var(--border-color,#333)', background: 'transparent', color: 'inherit' }}
              />
            )}
            <textarea
              value={bcBody}
              onChange={(e) => setBcBody(e.target.value)}
              rows={7}
              placeholder="Votre message…"
              style={{ width: '100%', maxWidth: 560, display: 'block', padding: '10px', borderRadius: 6, marginBottom: 12, border: '1px solid var(--border-color,#333)', background: 'transparent', color: 'inherit', resize: 'vertical' }}
            />
            <button
              className="schedule-regenerate-btn"
              onClick={sendBroadcast}
              disabled={bcSending}
            >
              {bcSending ? '⏳ Envoi…' : `📣 Envoyer à ${bcCount ?? '?'} utilisateur(s)`}
            </button>

            {/* Historique */}
            <h3 style={{ marginTop: 30 }}>Historique des diffusions</h3>
            {bcHistory.length === 0 ? (
              <p className="no-data">Aucune diffusion pour l'instant.</p>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {bcHistory.map(b => {
                  const audLabel = [
                    b.audienceVerified === 'verified' ? 'vérifiés' : b.audienceVerified === 'unverified' ? 'non vérifiés' : 'tous',
                    b.audienceCapital === 'invested' ? 'ont investi' : b.audienceCapital === 'notinvested' ? 'non investis' : 'capital: tous'
                  ].join(' · ');
                  const canaux = [b.channelInapp && '💬', b.channelEmail && '📧'].filter(Boolean).join(' ');
                  return (
                    <div key={b.id} style={{ border: '1px solid var(--border-color,#2a2a2a)', borderRadius: 8, padding: '10px 12px' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 10, fontSize: 12, opacity: 0.7 }}>
                        <span>{new Date(b.createdAt).toLocaleString('fr-FR')} · {canaux} · {audLabel}</span>
                        <span>{b.sentByName || ''}</span>
                      </div>
                      {b.subject && <div style={{ fontWeight: 600, marginTop: 4 }}>{b.subject}</div>}
                      <div style={{ marginTop: 4, whiteSpace: 'pre-line', fontSize: 14 }}>{b.body}</div>
                      <div style={{ marginTop: 6, fontSize: 12, opacity: 0.8 }}>
                        🎯 {b.targetedCount} ciblés
                        {b.channelInapp ? ` · 💬 ${b.inappCreated} in-app` : ''}
                        {b.channelEmail ? ` · 📧 ${b.emailsSent} envoyés${b.emailsFailed ? `, ${b.emailsFailed} échecs` : ''}${b.status !== 'done' ? ' (en cours…)' : ''}` : ''}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {activeTab === 'savings' && (
          <div className="admin-savings">
            <h2>💰 Portefeuilles épargne</h2>
            <p className="notif-email-hint" style={{ marginBottom: 16 }}>
              Portefeuilles retirables, séparés du trading (ne se multiplient pas). 1 propriétaire + 1 associé (optionnel), les deux peuvent retirer.
            </p>

            {/* Création */}
            <div style={{ border: '1px solid var(--border-color,#2a2f36)', borderRadius: 10, padding: 16, marginBottom: 22, maxWidth: 560 }}>
              <h3 style={{ marginTop: 0 }}>Créer un portefeuille</h3>
              <datalist id="savings-users-dl">
                {savingsUsers.map(u => <option key={u.id} value={u.username}>{u.email}</option>)}
              </datalist>
              <input
                type="text" placeholder="Nom (ex: Épargne famille)"
                value={savingsForm.name}
                onChange={(e) => setSavingsForm({ ...savingsForm, name: e.target.value })}
                style={{ width: '100%', padding: '9px 11px', borderRadius: 8, marginBottom: 10, border: '1px solid var(--border-color,#333)', background: 'transparent', color: 'inherit' }}
              />
              <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                <input
                  type="text" list="savings-users-dl" placeholder="Propriétaire (pseudo/email)"
                  value={savingsForm.owner}
                  onChange={(e) => { setSavingsForm({ ...savingsForm, owner: e.target.value }); searchSavingsUsers(e.target.value); }}
                  style={{ flex: 1, minWidth: 180, padding: '9px 11px', borderRadius: 8, border: '1px solid var(--border-color,#333)', background: 'transparent', color: 'inherit' }}
                />
                <input
                  type="text" list="savings-users-dl" placeholder="Associé (optionnel)"
                  value={savingsForm.associate}
                  onChange={(e) => { setSavingsForm({ ...savingsForm, associate: e.target.value }); searchSavingsUsers(e.target.value); }}
                  style={{ flex: 1, minWidth: 180, padding: '9px 11px', borderRadius: 8, border: '1px solid var(--border-color,#333)', background: 'transparent', color: 'inherit' }}
                />
              </div>
              <div style={{ display: 'flex', gap: 10, marginTop: 10, alignItems: 'center', flexWrap: 'wrap' }}>
                <input
                  type="text" inputMode="decimal" placeholder="Montant initial (0)"
                  value={savingsForm.amount}
                  onChange={(e) => setSavingsForm({ ...savingsForm, amount: e.target.value.replace(/[^\d.,]/g, '') })}
                  style={{ width: 180, maxWidth: '100%', padding: '9px 11px', borderRadius: 8, border: '1px solid var(--border-color,#333)', background: 'transparent', color: 'inherit' }}
                />
                <button className="schedule-regenerate-btn" onClick={handleCreateSavings}>Créer</button>
              </div>
            </div>

            {/* Liste */}
            <h3>Portefeuilles existants ({savingsList.length})</h3>
            {savingsList.length === 0 ? (
              <p className="no-data">Aucun portefeuille épargne.</p>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                {savingsList.map(w => (
                  <div key={w.id} style={{ border: '1px solid var(--border-color,#2a2f36)', borderRadius: 10, padding: '12px 14px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                      <div>
                        <div style={{ fontWeight: 700 }}>{w.name}</div>
                        <div style={{ fontSize: 12, opacity: 0.7 }}>
                          👤 {w.owner?.username || '—'}
                          {w.associate ? ` · 👥 ${w.associate.username}` : ' · (pas d\'associé)'}
                        </div>
                      </div>
                      <div style={{ fontSize: 20, fontWeight: 800, color: '#2dd4bf' }}>{formatCurrency(w.balance)}</div>
                    </div>
                    <div style={{ display: 'flex', gap: 8, marginTop: 10, flexWrap: 'wrap' }}>
                      <button className="btn-secondary" style={{ fontSize: 12, padding: '5px 12px' }} onClick={() => adjustSavingsWallet(w.id, 1)}>+ Créditer</button>
                      <button className="btn-secondary" style={{ fontSize: 12, padding: '5px 12px' }} onClick={() => adjustSavingsWallet(w.id, -1)}>− Débiter</button>
                      <button className="btn-secondary" style={{ fontSize: 12, padding: '5px 12px' }} onClick={() => setSavingsAssociate(w.id)}>👥 Associé</button>
                      <button className="btn-danger" style={{ fontSize: 12, padding: '5px 12px' }} onClick={() => deleteSavingsWallet(w)}>🗑️ Supprimer</button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {activeTab === 'system' && (
          <div className="admin-system">
            <h2>Actions Système</h2>

            <div className="system-actions">
              <div className="system-action-card">
                <h3>Régénérer les Plans</h3>
                <p>Régénère les plans mensuels pour tous les utilisateurs actifs.</p>
                <button onClick={() => handleSystemAction('regenerate-plans')}>
                  Exécuter
                </button>
              </div>

              <div className="system-action-card">
                <h3>Nettoyer les Expirées</h3>
                <p>Marque toutes les suggestions expirées comme "expired".</p>
                <button onClick={() => handleSystemAction('clear-expired')}>
                  Exécuter
                </button>
              </div>

              <div className="system-action-card">
                <h3>Rafraîchir le Cache</h3>
                <p>Recharge toutes les configurations depuis la base de données.</p>
                <button onClick={() => api.post('/admin/config/refresh').then(() => alert('Cache rafraîchi!'))}>
                  Exécuter
                </button>
              </div>
            </div>

            <div className="system-info">
              <h3>Informations Serveur</h3>
              <div className="info-grid">
                <div className="info-item">
                  <span className="info-label">Version:</span>
                  <span className="info-value">1.0.0</span>
                </div>
                <div className="info-item">
                  <span className="info-label">Environnement:</span>
                  <span className="info-value">{process.env.NODE_ENV || 'development'}</span>
                </div>
              </div>
            </div>
          </div>
        )}
      </main>

      {/* Modal de confirmation de transaction */}
      {showTransactionConfirm && pendingTransaction && (
        <div className="modal-overlay">
          <div className="modal-content transaction-confirm-modal">
            <div className="modal-header">
              <h2>Confirmation de Transaction</h2>
              <span className="modal-timestamp">{pendingTransaction.timestamp}</span>
            </div>

            <div className="modal-body">
              <div className="confirm-type-badge">
                <span className={`badge ${pendingTransaction.type}`}>
                  {pendingTransaction.type === 'credit' ? '+ CRÉDIT' : '- DÉBIT'}
                </span>
              </div>

              <div className="confirm-details">
                <div className="confirm-row">
                  <span className="confirm-label">Utilisateur</span>
                  <span className="confirm-value">{pendingTransaction.user.username}</span>
                </div>
                <div className="confirm-row">
                  <span className="confirm-label">Email</span>
                  <span className="confirm-value">{pendingTransaction.user.email}</span>
                </div>
                <div className="confirm-row">
                  <span className="confirm-label">ID Utilisateur</span>
                  <span className="confirm-value id">{pendingTransaction.user.id}</span>
                </div>
                <div className="confirm-divider"></div>
                <div className="confirm-row">
                  <span className="confirm-label">Montant</span>
                  <span className={`confirm-value amount ${pendingTransaction.type}`}>
                    {pendingTransaction.type === 'credit' ? '+' : '-'}{formatCurrency(pendingTransaction.amount)}
                  </span>
                </div>
                <div className="confirm-row">
                  <span className="confirm-label">Libellé</span>
                  <span className="confirm-value">{pendingTransaction.reason}</span>
                </div>
                <div className="confirm-divider"></div>
                <div className="confirm-row">
                  <span className="confirm-label">Solde actuel</span>
                  <span className="confirm-value">{formatCurrency(pendingTransaction.currentBalance)}</span>
                </div>
                <div className="confirm-row highlight">
                  <span className="confirm-label">Nouveau solde</span>
                  <span className="confirm-value">{formatCurrency(pendingTransaction.newBalance)}</span>
                </div>
              </div>

              <div className="confirm-warning">
                <span className="warning-icon">⚠️</span>
                <span>Cette action est irréversible. Vérifiez les informations avant de confirmer.</span>
              </div>
            </div>

            <div className="modal-actions">
              <button className="btn-cancel-modal" onClick={cancelTransaction}>
                Annuler
              </button>
              <button className={`btn-confirm-modal ${pendingTransaction.type}`} onClick={executeTransaction}>
                {pendingTransaction.type === 'credit' ? 'Confirmer le crédit' : 'Confirmer le débit'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Admin;
